import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View, Image, Text, ScrollView, TouchableOpacity, TouchableWithoutFeedback, TextInput,
  StyleSheet, Alert, Modal, ActivityIndicator, KeyboardAvoidingView,
  DeviceEventEmitter, RefreshControl, Platform, StatusBar,} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import ChipsRow from '../components/ChipsRow';
import AIMetadataDetails, { translateColor as translateAIColor } from '../components/AIMetadataDetails';
import Tooltip from '../components/Tooltip';
import CalendarIcon from '../components/CalendarIcon';
import AddOutfitToCalendarModal from '../components/AddOutfitToCalendarModal';
// v1.12.290 — try-on depuis Mes Looks
import TryOnModal from '../components/TryOnModal';
import FlatLayButton from '../components/FlatLayButton';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, spacing, radius, shadow } from '../theme';
import * as ImagePicker from 'expo-image-picker';
import { AWS_CONFIG } from '../config/aws_config';
import { SectionTitle, CTAButton, Badge } from '../components/UI';
import { wardrobe as MOCK_WARDROBE, wardrobeCategories, AI_CATEGORIES, AI_COLORS, AI_STYLES, looks } from '../data';
import { getWardrobe, getWardrobeWithEtag, addWardrobeItem, deleteWardrobeItem, deleteWardrobeItemFull } from '../services/DatabaseService';
import useWebBackToClose from '../hooks/useWebBackToClose';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import { getZalandoProducts } from '../services/ZalandoService';
import { getZaraProducts } from '../services/ZaraService';
import { getAmazonProducts } from '../services/AmazonService';
import { getAsosProducts } from '../services/AsosService';
import { getHMProducts } from '../services/HMService';
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { checkLimit, incrementAIUsage, FREE_LIMITS } from '../services/PremiumService';
import { usePremiumGuard } from '../hooks/usePremiumGuard';
import { purchaseSubscription } from '../services/IAPService';
import PremiumModal from '../components/PremiumModal';
import WardrobePhotoGuideModal from '../components/WardrobePhotoGuideModal';
import UpgradeModal from '../components/UpgradeModal';
import CreateLookModal from '../components/CreateLookModal';
import ProductDetailModal from '../components/ProductDetailModal';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import AddByUrlModal from '../components/AddByUrlModal';
import { showAppAlert } from '../components/AppDialog';
import WardrobeAddProgressModal from '../components/WardrobeAddProgressModal';
import { useI18n } from '../i18n';
import {
  generateSmartOutfits, detectMissingPieces, findCatalogSuggestions,
  analyzeItemImage,
} from '../services/AIStylistService';
import OutfitCard from '../components/OutfitCard';
import { displayName } from '../utils/displayName';
// v1.10.88 : optimisation image avant analyse IA (HEIC→JPEG, resize, compress)
import { optimizeImageForAI } from '../services/ImageOptimizer';
// v1.10.91 : auto-proxy pour CDN partenaires bloqués (TOUS, Zara, Cashmere)
import { getProxiedImageUrl } from '../utils/imageProxy';

/* ═══ Combinaisons possibles depuis la garde-robe ══════════════
 * Utilise l'algorithme AI Stylist pour scoring + détection manques
 * ══════════════════════════════════════════════════════════════ */
/**
 * Génère des combinaisons de tenues à partir du dressing.
 * @param {Array} items - articles du dressing
 * @param {number} maxCombos - nombre MAX de tenues à générer (selon tier abonnement)
 * @param {Array} catalog - produits partenaires pour suggestions
 * @param {number} tempC - température actuelle (°C) pour adapter les suggestions (optionnel)
 *
 * IMPORTANT : Cette fonction limite UNIQUEMENT le nombre de tenues générées.
 * Le nombre d'articles par tenue n'est PAS limité — l'algo IA décide librement
 * de combien d'articles inclure dans chaque look (3-5 typiquement : haut+bas+chaussures
 * +accessoires, ou robe+chaussures+sac, etc.).
 */
function generateCombinations(items, maxCombos, catalog, tempC) {
  if (!items || !Array.isArray(items) || items.length === 0) return [];
  maxCombos = Math.max(1, maxCombos || 5);
  tempC = tempC || 18;

  // Utiliser l'algo IA qui tient compte des métadonnées
  // Génère jusqu'à maxCombos tenues (limite abonnement) — pas de limite sur les articles par tenue
  const outfits = generateSmartOutfits(items, tempC, null, maxCombos);

  // Set partagé : éviter de répéter les mêmes articles catalogue entre combos
  const comboExcludeIds = new Set();

  // Enrichir chaque combo avec la détection des manques + suggestions
  const combos = outfits.map((o, idx) => {
    const missing = detectMissingPieces(o, tempC, 'Casual');
    let suggestions = [];
    if (catalog && catalog.length && missing.length) {
      const aiSugg = findCatalogSuggestions(o, missing, catalog, 2, comboExcludeIds);
      suggestions = Object.values(aiSugg).flat().slice(0, 4);
    }

    // Déterminer un label business selon les items
    const cats = o.items.map(i => i.category);
    let label = 'Combinaison';
    if (cats.includes('Robes')) label = 'Tenue complète';
    else if (cats.includes('Vestes')) label = 'Look structuré';
    else if (cats.includes('Hauts') && cats.includes('Bas')) label = 'Casual chic';
    if (missing.length > 0) label = missing.length > 1 ? 'À compléter' : label;

    return {
      id: `ai_${o.items.map(i => i.id).join('_')}_${idx}`,
      items: o.items,
      score: o.score,
      type: 'wardrobe',
      label,
      missing,
      suggestions,
    };
  });

  // Dédoublonner + cap final au nombre de tenues autorisées selon abonnement
  const seen = new Set();
  return combos.filter(c => {
    const key = c.items.map(i => i.id).sort().join(',');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, maxCombos);
}

// Suggestions d'articles manquants pour compléter les tenues
function getMissingPieceSuggestions(items) {
  if (!items || !Array.isArray(items)) return [];
  const suggestions = [];
  const cats = new Set(items.map(i => i.category));

  if (!cats.has('Chaussures'))
    suggestions.push({ category: 'Chaussures', emoji: '👟', reason: 'Complète toutes vos tenues', query: 'chaussures femme' });
  if (!cats.has('Sacs'))
    suggestions.push({ category: 'Sacs', emoji: '👜', reason: 'Accessoire indispensable', query: 'sac à main femme' });
  if (!cats.has('Vestes'))
    suggestions.push({ category: 'Vestes', emoji: '🧥', reason: 'Pour les looks superposés', query: 'veste femme' });
  if (!cats.has('Bas') && !cats.has('Robes'))
    suggestions.push({ category: 'Bas', emoji: '👖', reason: 'Pour varier vos looks', query: 'pantalon femme' });
  if (!cats.has('Hauts') && !cats.has('Robes'))
    suggestions.push({ category: 'Hauts', emoji: '👚', reason: 'Essentiel de garde-robe', query: 'top femme' });

  return suggestions.slice(0, 3);
}

// Construire des "Looks à shopper" : tenues qui démarrent avec 1-3 pièces du dressing
// et sont complétées par 5 à 10 articles du catalogue partenaire pour que
// l'utilisateur puisse compléter son look en 1 clic.
function buildMixedCombos(combinations, partnerProducts, ownedItems, gender, excludeSignatures) {
  if (!Array.isArray(ownedItems) || ownedItems.length === 0) return [];
  if (!Array.isArray(partnerProducts) || partnerProducts.length === 0) return [];
  const allProducts = partnerProducts.filter(p =>
    p && p.image && p.price > 0 && (p.originalUrl || p.affiliateUrl || p.url)
  );

  const excludeSet = excludeSignatures instanceof Set ? excludeSignatures : new Set();

  // Styles de looks complets : ancres (dressing) + pièces à shopper
  const STYLES = [
    { label: 'Soirée élégante',  emoji: '✨', anchors: ['Robes'],         wantsCatalog: ['Chaussures','Sacs','Bijoux','Accessoires','Vestes'] },
    { label: 'Casual chic',      emoji: '🌆', anchors: ['Hauts','Bas'],   wantsCatalog: ['Chaussures','Sacs','Vestes','Accessoires','Bijoux'] },
    { label: 'Bureau moderne',   emoji: '💼', anchors: ['Hauts','Bas'],   wantsCatalog: ['Vestes','Chaussures','Sacs','Bijoux','Accessoires'] },
    { label: 'Bohème estival',   emoji: '🌸', anchors: ['Robes'],         wantsCatalog: ['Chaussures','Sacs','Accessoires','Bijoux','Vestes'] },
    { label: 'Look street',      emoji: '🎧', anchors: ['Hauts','Bas'],   wantsCatalog: ['Vestes','Chaussures','Accessoires','Sacs','Bijoux'] },
    { label: 'Minimal chic',     emoji: '⚪', anchors: ['Hauts','Bas'],   wantsCatalog: ['Vestes','Chaussures','Sacs','Accessoires'] },
    { label: 'Romantique',       emoji: '🌹', anchors: ['Robes'],         wantsCatalog: ['Chaussures','Sacs','Bijoux','Accessoires'] },
    { label: 'Week-end décontracté', emoji: '🌿', anchors: ['Hauts','Bas'], wantsCatalog: ['Chaussures','Accessoires','Sacs','Vestes'] },
    { label: 'Soirée cocktail',  emoji: '🥂', anchors: ['Robes'],         wantsCatalog: ['Chaussures','Sacs','Bijoux','Accessoires'] },
  ];

  // Shuffle styles pour varier les résultats à chaque appel
  const shuffledStyles = [...STYLES].sort(() => Math.random() - 0.5);

  const results = [];
  const usedProductIds = new Set();
  const usedOwnedIds = new Set();

  const pickOwned = (cat) => {
    const cands = ownedItems.filter(i => i.category === cat && !usedOwnedIds.has(i.id));
    if (cands.length > 0) {
      const it = cands[Math.floor(Math.random() * cands.length)];
      usedOwnedIds.add(it.id);
      return { ...it, owned: true };
    }
    const any = ownedItems.find(i => i.category === cat);
    return any ? { ...any, owned: true } : null;
  };

  const pickCatalog = (cat) => {
    let cands = allProducts.filter(p => p.category === cat && !usedProductIds.has(p.id));

    // ═══ Re-valider la catégorie via nom (évite jupe dans Robes etc.) ═══
    try {
      const { filterMiscategorized } = require('../services/GenderFilter');
      cands = filterMiscategorized(cands, cat);
    } catch (e) {}

    // ═══ FILTRE SACS FEMME ═══ : exclure sac à dos sportifs, prioriser handbag/clutch/tote
    if (cat === 'Sacs') {
      try {
        const { isBagAppropriate, scoreBagGender } = require('../services/AIStylistService');
        cands = cands.filter(p => isBagAppropriate(p, '', 3, gender));
        // Tri gender-aware pour prioriser les sacs femme
        cands.sort((a, b) => scoreBagGender(b, gender) - scoreBagGender(a, gender));
      } catch {}
    }

    if (cands.length === 0) return null;
    const scored = cands.map(p => {
      let s = 50;
      if (p.partner === 'cashmere' || p.partner === 'iqueens') s += 30;
      if (p.discount && p.discount > 20) s += 15;
      if (p.rating && p.rating >= 4) s += 10;
      // Bonus gender-aware pour sacs
      if (cat === 'Sacs') {
        try {
          const { scoreBagGender } = require('../services/AIStylistService');
          s += (scoreBagGender(p, gender) - 50) * 0.4;
        } catch {}
      }
      return { p, s };
    }).sort((a, b) => b.s - a.s);
    const pool = scored.slice(0, Math.min(15, scored.length));
    const total = pool.reduce((x, y) => x + y.s, 0);
    let rnd = Math.random() * total;
    let chosen = pool[0].p;
    for (const x of pool) { rnd -= x.s; if (rnd <= 0) { chosen = x.p; break; } }
    usedProductIds.add(chosen.id);
    return { ...chosen, owned: false };
  };

  for (const style of shuffledStyles) {
    const comboItems = [];
    // 1. Ancres du dressing (1-2 pièces possédées)
    let anchorsFound = 0;
    for (const cat of style.anchors) {
      const own = pickOwned(cat);
      if (own) { comboItems.push(own); anchorsFound++; }
    }
    if (anchorsFound === 0) continue;

    // 2. Articles catalogue à shopper : cibler 5 à 10 pièces complémentaires
    let newCount = 0;
    let totalPrice = 0;
    // 1er passage : 1 article par catégorie manquante
    for (const cat of style.wantsCatalog) {
      if (newCount >= 10) break;
      if (ownedItems.some(i => i.category === cat)) continue;
      const product = pickCatalog(cat);
      if (product) {
        comboItems.push(product);
        newCount++;
        totalPrice += product.price || 0;
      }
    }
    // 2e passage : si moins de 5, on pousse des doublons par catégorie populaire
    if (newCount < 5) {
      for (const cat of style.wantsCatalog) {
        if (newCount >= 7) break;
        const extra = pickCatalog(cat);
        if (extra) {
          comboItems.push(extra);
          newCount++;
          totalPrice += extra.price || 0;
        }
      }
    }

    if (newCount >= 3 && totalPrice > 0) {
      // Calculer signature unique du combo (style + items ids triés)
      const signature = style.label + ':' + comboItems.map(i => i.id).sort().join('|');
      if (excludeSet.has(signature)) {
        // Combo déjà vu → skipper ET libérer les items pris pour essayer un autre style
        for (const it of comboItems) {
          if (it.owned) usedOwnedIds.delete(it.id);
          else usedProductIds.delete(it.id);
        }
        continue;
      }

      results.push({
        label:    style.label,
        emoji:    style.emoji,
        items:    comboItems,
        price:    Math.round(totalPrice),
        newCount,
        signature,
      });
    }
  }

  return results.slice(0, 50); // cap max 50 combos
}

// v1.12.231 — Manteaux ajouté entre Vestes et Bas (long ≥ mi-cuisse vs Vestes courtes)
const W_CATS_F = ['Tout','Robes','Hauts','Vestes','Manteaux','Bas','Chaussures','Sacs','Bijoux','Accessoires'];
const W_CATS_M = ['Tout','Hauts','Vestes','Manteaux','Bas','Chaussures','Sacs','Accessoires'];
function getWardrobeCats(g) {
  return g === 'M' ? W_CATS_M : W_CATS_F;
}

// v1.12.902 — Image dressing avec fallback emoji si chargement échoue
// Force HTTPS via getProxiedImageUrl, fallback automatique si 404 / cleartext block
function WardrobeItemImage({ uri, emoji, hidden }) {
  const [failed, setFailed] = React.useState(false);
  if (failed || !uri) {
    return <Text style={[s.gridEmoji, hidden && { opacity: 0.4 }]}>{emoji || '👗'}</Text>;
  }
  return (
    <Image
      source={{ uri: getProxiedImageUrl(uri) }}
      style={[s.gridImgPhoto, hidden && { opacity: 0.4 }]}
      blurRadius={hidden ? 8 : 0}
      onError={() => setFailed(true)}
    />
  );
}

export default function WardrobeScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { t, lang } = useI18n();
  const { user, getValidToken, updateUserProfile } = useAuth();
  const insets = useSafeAreaInsets();
  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();
  // ═══ FIX 2026-04 : ne jamais démarrer avec MOCK_WARDROBE ═══
  // Avant : useState(MOCK_WARDROBE) → les nouveaux utilisateurs voyaient
  // 6 articles fictifs pendant 1-2s le temps du fetch réseau. Effet
  // visuel trompeur : on a l'impression d'avoir un dressing qui n'est
  // pas le nôtre.
  // Maintenant : []. Le cache local AsyncStorage (loadWardrobe) remplit
  // instantanément si disponible, sinon écran vide jusqu'au fetch DB.
  const [items,      setItems]      = useState([]);
  const [dbLoading,  setDbLoading]  = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try { await loadWardrobe(true); } catch {}
    setTimeout(() => setRefreshing(false), 600);
  }, []);

  // Charger la garde-robe depuis la DB
  // ═══ Refs pour optimisation chargement ═══
  const lastLoadTsRef = React.useRef(0);         // timestamp du dernier loadWardrobe réussi
  const aiBatchRunningRef = React.useRef(false); // évite lancer 2 batchs IA simultanés

  const loadWardrobe = React.useCallback(async (force = false) => {
    if (!user) return;

    // ═══ THROTTLE : évite les reloads en rafale ═══
    // Si on a chargé il y a < 8s et qu'on a déjà des items → skip (sauf si force=true)
    const now = Date.now();
    if (!force && items.length > 0 && (now - lastLoadTsRef.current) < 8000) {
      console.log('[Wardrobe] 🚫 load throttled (< 8s ago)');
      return;
    }

    setDbLoading(true);
    const CACHE_KEY = `outfit_wardrobe_cache_${user.id}`;

    try {
      // ═══ 1. AFFICHAGE INSTANTANÉ depuis cache local (< 100ms) ═══
      // Permet à l'UI de montrer le dressing pendant le fetch réseau
      // v1.12.567 — on lit aussi l'ETag pour la requête conditionnelle
      let cachedEtag = null;
      let cachedItems = null;
      try {
        const cached = await AsyncStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.items && Array.isArray(parsed.items)) {
            cachedItems = parsed.items;
            cachedEtag = parsed.etag || null;
            if (items.length === 0) {
              setItems(parsed.items);
              console.log(`[Wardrobe] 💾 cache hit: ${parsed.items.length} items (age=${Math.round((now - parsed.ts)/1000)}s, etag=${cachedEtag ? cachedEtag.slice(0,8) : 'none'})`);
            }
          }
        }
      } catch {}

      // ═══ 2. FETCH RÉSEAU (DB) avec ETag + gzip ═══
      // v1.12.567 — envoie If-None-Match → si 304 on garde le cache (50ms vs 300ms)
      const token = await getValidToken();
      let arr = null;
      try {
        const result = await getWardrobeWithEtag(user.id, token, cachedEtag);
        if (result.status === 304) {
          console.log(`[Wardrobe] ⚡ 304 Not Modified — cache valide (${cachedItems?.length || 0} items)`);
          arr = cachedItems || [];
          lastLoadTsRef.current = now;
        } else {
          arr = Array.isArray(result.data) ? result.data : [];
          arr = [...arr].sort((a, b) => {
            const ta = new Date(a?.created_at || 0).getTime();
            const tb = new Date(b?.created_at || 0).getTime();
            return tb - ta;
          });
          setItems(arr);
          lastLoadTsRef.current = now;
          try {
            await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now, items: arr, etag: result.etag }));
          } catch {}
        }
      } catch (fetchErr) {
        console.warn(`[Wardrobe] ⚠️ fetch failed, keeping cache: ${fetchErr.message}`);
      }
      if (arr === null) {
        setDbLoading(false);
        return;
      }

      // ═══ 3. ANALYSE IA EN BATCH (optimisée) ═══
      //    - Skip si un batch tourne déjà
      //    - Batch limité à 5 articles par ouverture
      //    - Arrête si la limite IA journalière est atteinte
      //    - Persiste tout de suite dans le cache local
      if (arr.length > 0 && token && !aiBatchRunningRef.current) {
        const unanalyzed = arr.filter(i => i && i.image && !i.ai_metadata?.analyzedAt);
        const BATCH_LIMIT = 5;
        const toAnalyze = unanalyzed.slice(0, BATCH_LIMIT);

        if (toAnalyze.length > 0) {
          aiBatchRunningRef.current = true;
          console.log(`[Wardrobe] starting AI analysis: ${toAnalyze.length}/${unanalyzed.length} items`);
          // Analyser en séquentiel, sans bloquer l'UI
          (async () => {
            let done = 0;
            try {
              for (const item of toAnalyze) {
                // ═══ FIX 2026-04 : suppression du double check checkLimit ═══
                // Avant : on appelait checkLimit('ai', 0, user) AVANT chaque
                //   analyzeItemImage. Ce checkLimit faisait un fetch GET
                //   /ai-usage/status qui ajoutait ~500-2000ms par item.
                //   Pour 5 items → +2-10s de latence inutile sur le batch.
                // Maintenant : analyzeItemImage gère lui-même le quota via
                //   guardAIQuota (qui appelle /ai-usage/check-and-increment).
                //   Si quota dépassé, metadata._quotaExceeded → on break (ligne ~390).

                try {
                  // v1.12.305 — batch background sur items déjà dans le dressing :
                  // ne PAS compter dans le quota IA (seul "ajout au dressing" doit compter).
                  // On passe user=null pour skip guardAIQuota dans analyzeItemImage.
                  const metadata = await analyzeItemImage({
                    id: item.id,
                    name: item.name,
                    category: item.category,
                    image: item.image || item.image_url,
                  }, token, null);
                  // ═══ FIX 2026-04 : stopper le batch si quota dépassé ═══
                  if (metadata && metadata._quotaExceeded) {
                    console.log('[Wardrobe] ⛔ quota IA épuisé pendant batch, arrêt');
                    break;
                  }
                  if (metadata) {
                    // v1.13.89 — NE PLUS réécrire le cache (JSON.stringify du dressing
                    //   entier) à CHAQUE item : c'était bloquant et répété → lenteur.
                    //   On écrit UNE fois en fin de batch (voir après la boucle).
                    setItems(prev => prev.map(it =>
                      it.id === item.id ? { ...it, ai_metadata: metadata } : it
                    ));
                    done++;
                    console.log(`[Wardrobe] ✨ analysé (${done}/${toAnalyze.length}): ${item.name}`);
                  }
                } catch (e) {
                  console.warn(`[Wardrobe] analyse ${item.id}:`, e.message);
                }
              }
              console.log(`[Wardrobe] 🎉 batch terminé: ${done}/${toAnalyze.length}` +
                (unanalyzed.length > BATCH_LIMIT ? ` (${unanalyzed.length - BATCH_LIMIT} articles restants)` : ''));
              if (done > 0) {
                // v1.13.89 — écriture cache UNE seule fois en fin de batch (au lieu
                //   de par-item) : on relit le state courant via un setItems no-op.
                setItems(prev => {
                  AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: prev })).catch(() => {});
                  return prev;
                });
                try { DeviceEventEmitter.emit('wardrobe_updated', { aiAnalyzed: done, _internalSkipReload: true }); } catch {}
              }
            } finally {
              aiBatchRunningRef.current = false;
            }
          })();
        }
      }
    } catch (e) {
      console.warn('loadWardrobe:', e.message);
    } finally {
      setDbLoading(false);
    }
  }, [user?.id, items.length]);

  // Chargement au montage
  React.useEffect(() => { loadWardrobe(); }, [loadWardrobe]);

  // Écouter l'event global 'wardrobe_updated' émis après un achat confirmé
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    let debounceTimer = null;

    const sub = DeviceEventEmitter.addListener('wardrobe_updated', (data) => {
      // ═══ Skip reload si c'est un event interne (ex: fin d'analyse IA) ═══
      // Les items sont déjà à jour dans le state, pas besoin de refetch
      if (data?._internalSkipReload) {
        console.log('[Wardrobe] event skipped (internal AI batch)');
        return;
      }

      // Si un nouvel article est fourni, l'ajouter directement en local pour affichage immédiat
      if (data?.newItem) {
        const normalizedItem = {
          ...data.newItem,
          image: data.newItem.image || data.newItem.image_url || null,
          emoji: data.newItem.emoji || '👗',
        };
        setItems(prev => {
          const exists = prev.some(i => String(i.id) === String(normalizedItem.id));
          if (exists) return prev;
          return [normalizedItem, ...prev];
        });
      }

      // ═══ DEBOUNCE 500ms : plusieurs events rapprochés = 1 seul reload ═══
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { loadWardrobe(true); }, 500);
    });

    return () => {
      sub.remove();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [loadWardrobe]);
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('Tout');
  const [selected,   setSelected]   = useState(null);
  // v1.12.568 — back navigateur ferme le modal détail item au lieu de quitter
  useWebBackToClose(!!selected, useCallback(() => setSelected(null), []));

  // v1.12.220+222 — Reset à l'état initial quand l'utilisateur quitte un onglet
  // à filtres. Inclut le sous-onglet Dressing (Mes pièces / Mes looks) qui revient
  // par défaut sur "Mes pièces" en sortant de l'écran.
  React.useEffect(() => {
    const { DeviceEventEmitter } = require('react-native');
    const sub = DeviceEventEmitter.addListener('tabs_reset_to_default', () => {
      setSearch('');
      setCategory('Tout');
      setSelected(null);
      try { setTab('items'); } catch {}
      try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
    });
    return () => sub.remove();
  }, []);
  // v1.12.119 — popup progression de l'ajout (upload + analyse IA)
  // null | 'uploading' | 'analyzing' | 'done' | 'error'
  const [addProgress, setAddProgress] = useState(null);
  const [addProgressError, setAddProgressError] = useState(null);
  const [showAdd,    setShowAdd]     = useState(false);
  const [showAddUrl, setShowAddUrl]  = useState(false);
  // v1.12.194 — guide photo affiché AU 1er ajout (Claude AI Vision-friendly)
  const [showPhotoGuide, setShowPhotoGuide] = useState(false);

  // Helper : à CHAQUE click "+" on affiche le guide photo, SAUF si l'user a
  // explicitement coché "Ne plus afficher" auparavant. Le flag est en
  // AsyncStorage par user.
  const handleOpenAdd = React.useCallback(async () => {
    // v1.13.85 — FIX "popup analyse IA épuisée qui s'affiche et disparaît au clic +" :
    //   avant, le paywall (affiché par l'effet focus) était DISMISSÉ dès que le modal
    //   guide/ajout s'ouvrait par-dessus (conflit 2-Modals Android). Désormais, si le
    //   quota d'analyses IA est épuisé, on affiche le paywall PROPREMENT et on N'OUVRE
    //   PAS le flux d'ajout (l'analyse échouerait faute de quota) → aucun modal
    //   concurrent, plus de flash. (guardCheck 'ai' tient compte des scans achetés.)
    const okAi = await guardCheck('ai', 0, false);
    if (!okAi) return; // paywall affiché par guardCheck, pas d'ouverture du flux d'ajout
    try {
      const uid = user?.id || 'guest';
      const key = `wardrobe_photo_guide_optout_${uid}`;
      const optout = await AsyncStorage.getItem(key);
      if (optout === '1') {
        setShowAdd(true);
        return;
      }
    } catch {}
    setShowPhotoGuide(true);
  }, [user?.id, guardCheck]);

  // dontShowAgain (booléen) = true si l'user a coché la case dans la modale.
  const handlePhotoGuideContinue = React.useCallback(async (dontShowAgain) => {
    if (dontShowAgain) {
      try {
        const uid = user?.id || 'guest';
        await AsyncStorage.setItem(`wardrobe_photo_guide_optout_${uid}`, '1');
      } catch {}
    }
    setShowPhotoGuide(false);
    setShowAdd(true);
  }, [user?.id]);
  // v1.13.58 — onClose STABLE (useCallback) OBLIGATOIRE : le WardrobePhotoGuideModal
  // s'appuie sur AppModal qui, sur web, pousse une entrée d'historique. Un onClose
  // arrow inline = nouvelle réf à chaque render → relance le cycle history.back →
  // le guide "s'affiche puis disparaît" aussitôt. Cf. [[appmodal-callback-stable]].
  const handlePhotoGuideClose = React.useCallback(() => setShowPhotoGuide(false), []);
  const [showCreateLook, setShowCreateLook] = useState(false);
  const [preselectForLook, setPreselectForLook] = useState(null);
  const [userCreatedLooks, setUserCreatedLooks] = useState([]);
  // v1.12.319 — recherche dans Mes Looks (nom look + nom/pseudo article + cat + couleur + dressing)
  const [looksSearchQ, setLooksSearchQ] = useState('');
  // v1.12.290 — try-on depuis Mes Looks
  const [tryOnOutfit, setTryOnOutfit] = useState(null);
  const [tryOnAuthToken, setTryOnAuthToken] = useState(null);
  const openTryOnVertex = React.useCallback(async (outfit) => {
    if (!outfit?.items?.length) return;
    if (!user) {
      showAppAlert(t('login_required'), t('login_required_for_tryon'));
      return;
    }
    // v1.12.620 — Plus de shortcut "tier === free reject" : un Free avec
    // free_onboarding_tryon ou add-ons doit pouvoir ouvrir. Le pre-check
    // fetchTryOnUsage ci-dessous utilise la limit backend qui inclut le bonus.
    try {
      const token = getValidToken ? await getValidToken() : null;
      // v1.12.491 — Pre-check quota AVANT ouverture modal (évite crash SIGABRT iOS)
      if (token) {
        try {
          const { fetchTryOnUsage } = require('../services/TryOnService');
          const u = await fetchTryOnUsage(token);
          if (u && (u.limit === 0 || u.count >= u.limit)) {
            DeviceEventEmitter.emit('tryon_limit_reached', {
              tier: u.tier, limit: u.limit, current: u.count,
              isPaidTier: u.isPaidTier, nextResetDate: u.nextResetDate,
            });
            return;
          }
        } catch (e) { console.warn('[Wardrobe] tryon quota check failed:', e?.message); }
      }
      setTryOnAuthToken(token);
      setTryOnOutfit(outfit);
    } catch (e) {
      console.warn('[Wardrobe] openTryOn failed:', e?.message);
    }
  }, [user, getValidToken, t]);
  // v1.12.250 — Raccourci ajout calendrier depuis Mes Looks
  const [calendarShortcut, setCalendarShortcut] = useState(null);
  const [loadingRefreshLookId, setLoadingRefreshLookId] = useState(null);
  const [expandedLookIds, setExpandedLookIds] = useState({}); // { [lookId]: true } → mode grille 2 cols
  // ═══ FIX TDZ web (2026-05) : déclarations remontées ═══
  // Avant : ces hooks étaient lignes 915-1002, après plusieurs callbacks/
  //   effects qui les référencent (refreshLookSuggestions deps, combinations
  //   useMemo, useFocusEffect guardCheck, etc.). Sur Hermes natif ça passait,
  //   mais sur web (V8 + Metro minification) ça throw "Cannot access 'X'
  //   before initialization" au render. Solution : déclarer avant la 1ère
  //   utilisation.
  const [partnerProducts, setPartnerProducts] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingMoreCombos, setLoadingMoreCombos] = useState(false);
  const { showPremium, premiumTrigger, limitInfo, guardCheck, closePremium, setPremiumTrigger, setShowPremium, setLimitInfo } = usePremiumGuard(user);
  // ═══ États pour ProductDetailModal + PurchaseConfirmModal (partagés app) ═══
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [boughtProduct,   setBoughtProduct]   = useState(null);

  // v1.12.286 — looks persistés en DB via LookService (avec cache AsyncStorage)
  // v1.12.898 — Perf : throttle 30s pour éviter refetch à chaque focus tab,
  //   SAUF si la liste est vide (force fresh fetch) → garantit affichage initial
  //   et après login. Bypass total via opts.force.
  // v1.12.913 — Fix "anciens looks pas chargés à l'ouverture" :
  //   Au mount, le token Cognito n'est parfois pas encore prêt → listLooks
  //   tombait sur readCache(uid) qui retournait []. Quand l'user créait un
  //   nouveau look, le token était devenu disponible entre-temps et la
  //   liste complète apparaissait. Fix : déclenche force-refetch aussi quand
  //   user?.accessToken passe de undefined → string.
  const lastFetchRef = React.useRef(0);
  const userCreatedLooksRef = React.useRef([]);
  const loadUserCreatedLooks = React.useCallback(async (opts = {}) => {
    const now = Date.now();
    const cacheEmpty = !userCreatedLooksRef.current || userCreatedLooksRef.current.length === 0;
    if (!opts.force && !cacheEmpty && now - lastFetchRef.current < 30000) {
      return; // throttle uniquement si on a déjà des données récentes
    }
    // v1.12.913 — Si pas de token Cognito prêt ET pas user.id (guest), skip
    // pour éviter de vider la liste pendant que l'auth charge.
    if (!user?.accessToken && !user?.id) {
      console.log('[Wardrobe] listLooks skip: auth pas prête, garde la liste actuelle');
      return;
    }
    lastFetchRef.current = now;
    try {
      const { listLooks } = require('../services/LookService');
      const list = await listLooks({ user, getValidToken });
      // v1.12.913 — Si la réponse est vide mais qu'on avait déjà des items,
      // c'est probablement un fetch qui a échoué silencieusement (token pas prêt
      // côté backend, 304 sur cache vide, etc.). On garde la liste existante.
      if ((!list || list.length === 0) && userCreatedLooksRef.current.length > 0) {
        console.log('[Wardrobe] listLooks renvoie vide mais cache non vide → on garde l\'existant');
        return;
      }
      userCreatedLooksRef.current = list || [];
      setUserCreatedLooks(list || []);
    } catch(e) { console.warn('[Wardrobe] listLooks failed:', e?.message); }
  }, [user, getValidToken]);
  // Mount initial OU changement d'user OU token enfin disponible → force refetch
  // v1.12.913 — dep user?.accessToken garantit refetch dès que le JWT est prêt
  React.useEffect(() => {
    loadUserCreatedLooks({ force: true });
  }, [user?.id, user?.accessToken]);
  // Focus → throttled (pas de refetch si déjà fait il y a < 30s avec données)
  useFocusEffect(React.useCallback(() => { loadUserCreatedLooks(); }, [loadUserCreatedLooks]));

  // Rafraîchir / étendre les suggestions d'un look créé
  // append=false → remplace, append=true → étend avec nouveaux articles
  const refreshLookSuggestions = React.useCallback(async (look, append = false) => {
    console.log(`[RefreshLook] 🔄 called for look ${look.id}, append=${append}, current suggs=${(look.suggestions||[]).length}`);
    if (loadingRefreshLookId === look.id) {
      console.log(`[RefreshLook] already loading, skip`);
      return;
    }
    setLoadingRefreshLookId(look.id);
    try {
      const { detectMissingPieces, findCatalogSuggestions } = require('../services/AIStylistService');
      const { suggestCatalogItems } = require('../services/OutfitMatchingService');
      // v1.12.218 — re-ranking final via OutfitEngine pour assurer le meilleur score de tenue
      const { computeCohesion } = require('../services/OutfitEngine');

      // Filtre strict : seulement vraies images HTTPS
      const isValid = (p) => {
        if (!p || !p.id) return false;
        const img = p.image || p.image_url;
        if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
        const link = p.affiliateUrl || p.originalUrl || p.url || p.link;
        if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
        if (!(p.price > 0)) return false;
        return true;
      };

      // ═══ Si partnerProducts vide/insuffisant, charger le catalogue à la volée ═══
      let validCatalog = (partnerProducts || []).filter(isValid);
      console.log(`[RefreshLook] partnerProducts: ${(partnerProducts || []).length}, valid: ${validCatalog.length}`);

      if (validCatalog.length < 50) {
        console.log('[RefreshLook] catalog insufficient, fetching dynamic catalog…');
        try {
          const { fetchDynamicCatalog } = require('../services/DynamicCatalogService');
          const { getZalandoProducts } = require('../services/ZalandoService');
          const { getAsosProducts }    = require('../services/AsosService');
          const { getZaraProducts }    = require('../services/ZaraService');
          const { getHMProducts }      = require('../services/HMService');
          const g = user?.gender || 'F';

          const [dyn, z, a, zr, h, zb, ab, zs] = await Promise.allSettled([
            fetchDynamicCatalog(g),
            getZalandoProducts(g, 'Tout', 30),
            getAsosProducts(g, 'Tout', 30),
            getZaraProducts(g, 'Tout'),
            getHMProducts(g, 'Tout', 20),
            getZalandoProducts(g, 'Bijoux', 15),
            getAsosProducts(g, 'Bijoux', 15),
            getZalandoProducts(g, 'Sacs', 15),
          ]);
          const fetched = [
            ...(dyn.value || []),
            ...(z.value || []), ...(a.value || []), ...(zr.value || []), ...(h.value || []),
            ...(zb.value || []), ...(ab.value || []), ...(zs.value || []),
          ].filter(isValid);

          // Déduper
          const seen = new Set();
          const deduped = [];
          for (const p of fetched) {
            if (seen.has(p.id)) continue;
            seen.add(p.id);
            deduped.push(p);
          }
          validCatalog = deduped;
          console.log(`[RefreshLook] fetched fresh catalog: ${validCatalog.length} valid products`);
          // Mettre à jour partnerProducts pour les prochains appels
          if (validCatalog.length > 0) {
            setPartnerProducts(validCatalog);
          }
        } catch(e) {
          console.warn('[RefreshLook] dynamic fetch failed:', e.message);
        }
      }

      if (validCatalog.length === 0) {
        Alert.alert('Catalogue indisponible', 'Impossible de charger le catalogue. Vérifiez votre connexion et réessayez.');
        setLoadingRefreshLookId(null);
        return;
      }

      // ═══ v1.12.218 : Pre-filtre genre AVANT scoring (cohérent avec l'utilisateur) ═══
      // L'IA filtre déjà par ai_metadata.gender, mais on durcit en amont pour
      // éviter les items unisex non-analysés mal genrés.
      const userGender = user?.gender || 'F';
      const genderFiltered = validCatalog.filter(p => {
        const g = p.ai_metadata?.gender || p.gender;
        if (!g || g === 'unisex' || g === 'N') return true; // unisex/inconnu : on laisse passer
        return g === userGender;
      });
      // Garde-fou : si trop strict (< 30 items), on garde tout
      const baseCatalog = genderFiltered.length >= 30 ? genderFiltered : validCatalog;
      console.log(`[RefreshLook] gender=${userGender} filter: ${baseCatalog.length}/${validCatalog.length} items`);

      // Shuffle + exclude déjà proposés si append=true
      const existingIds = append ? new Set((look.suggestions || []).map(s => s.id)) : new Set();
      const pool = baseCatalog.filter(p => !existingIds.has(p.id));
      const shuffled = [...pool].sort(() => Math.random() - 0.5);

      // ═══ Enrichir le pseudoOutfit avec contexte IA (température saisonnière) ═══
      // Permet à findCatalogSuggestions d'utiliser ai_metadata.seasonality/temperatureRange
      // et de filtrer les articles incompatibles avec la saison actuelle
      const month = new Date().getMonth(); // 0=Jan
      const approxTempC = [5, 6, 10, 14, 18, 22, 25, 25, 21, 16, 10, 6][month]; // France moyennes
      const pseudoOutfit = {
        items: look.items || [],
        tempC: approxTempC,
        occasion: look.occasion || 'Casual',
        gender: user?.gender || 'F',
      };
      const ownedCats = new Set((look.items || []).map(i => i.category));

      // ═══ LOGIQUE CIBLÉE : catégories à compléter selon ce qui est dans le look ═══
      let targetCats = [];
      if (ownedCats.has('Robes')) {
        // Robe → pas de Hauts/Bas à suggérer, focus accessoires + alternatives
        targetCats = ['Chaussures', 'Sacs', 'Accessoires', 'Bijoux', 'Vestes'];
      } else if (ownedCats.has('Hauts')) {
        // Haut présent → compléter avec Bas + accessoires
        targetCats = ['Bas', 'Chaussures', 'Sacs', 'Accessoires', 'Bijoux', 'Vestes'];
      } else {
        // Fallback : detectMissingPieces intelligent
        const missing = detectMissingPieces(pseudoOutfit, approxTempC, look.occasion || 'Casual');
        targetCats = missing.map(m => typeof m === 'string' ? m : m.category);
      }
      const missingStructured = targetCats
        .filter(cat => !ownedCats.has(cat) || cat === 'Bijoux' || cat === 'Accessoires')
        .map(cat => ({ category: cat, reason: `Compléter avec ${cat.toLowerCase()}` }));

      // ═══ v1.12.180 : alternatives pour les pièces déjà dans le look ═══
      // Pour chaque article du look, on cherche un meilleur catalogue qui a un
      // score plus haut. L'utilisateur peut "upgrader" sa tenue.
      // Exclusion : pas de doublon avec les categories déjà ciblées en missing.
      const missingCatsSet = new Set(missingStructured.map(m => m.category));
      const replaceTargets = (look.items || [])
        .filter(it => it && it.id && !missingCatsSet.has(it.category))
        .map(item => ({
          category:       item.category,
          reason:         `Alternative à votre ${(item.category || '').toLowerCase()}`,
          replacesItemId: item.id,
          replacesItem:   item,
          referenceColor: item.color || item.ai_metadata?.primaryColor,
        }));

      const allTargets = [...missingStructured, ...replaceTargets];
      const replaceCatMap = new Map(replaceTargets.map(r => [r.category, r]));

      console.log(`[RefreshLook] ${look.id}: ownedCats=[${[...ownedCats].join(',')}] → missing=[${missingStructured.map(m=>m.category).join(',')}] replace=[${replaceTargets.map(r=>r.category).join(',')}] tempC=${approxTempC}`);

      // ═══ PLUS DE CHOIX : 4 items par catégorie au lieu de 2 ═══
      // Sur 5-6 catégories, ça fait 20-24 articles diversifiés
      // v1.12.218 — on demande un peu plus de candidats (6) puis on re-rank via OutfitEngine
      const ITEMS_PER_CAT = 6;
      const aiSugg = findCatalogSuggestions(pseudoOutfit, allTargets, shuffled, ITEMS_PER_CAT, new Set());
      // Marquer les suggestions de remplacement avec _replacesItemId pour
      // que l'UI puisse afficher "Remplace votre X".
      for (const [cat, items] of Object.entries(aiSugg)) {
        const rt = replaceCatMap.get(cat);
        if (rt) {
          for (const it of items) {
            it._replacesItemId = rt.replacesItemId;
            it._replacesItem   = rt.replacesItem;
            it._isReplacement  = true;
          }
        }
      }

      // ═══ v1.12.218 : Re-ranking par OutfitEngine.computeCohesion ═══
      //   Pour chaque candidat suggéré, on construit un "virtual outfit" =
      //   look.items + candidat (en remplaçant si _isReplacement). On calcule
      //   le score de cohésion global et on re-trie pour faire remonter les
      //   items qui maximisent le score de tenue complète.
      try {
        const baseItems = look.items || [];
        const targetTag = (look.style || '').toLowerCase() || (look.occasion || '').toLowerCase() || null;
        const KEEP_PER_CAT = 4; // après re-rank on garde top 4
        for (const [cat, items] of Object.entries(aiSugg)) {
          if (!Array.isArray(items) || items.length === 0) continue;
          const scored = items.map(candidate => {
            // Virtual outfit : retire l'item remplacé puis ajoute le candidat
            let virtualItems;
            if (candidate._isReplacement && candidate._replacesItemId) {
              virtualItems = baseItems.filter(it => it.id !== candidate._replacesItemId).concat(candidate);
            } else {
              virtualItems = baseItems.concat(candidate);
            }
            let cohesion = 0;
            try {
              const c = computeCohesion(virtualItems, approxTempC, targetTag, null, null);
              cohesion = (c?.total || 0);
            } catch {}
            return { candidate, cohesion };
          });
          // Tri DESC par cohésion (= meilleur score outfit avec ce candidat)
          scored.sort((a, b) => b.cohesion - a.cohesion);
          // Annoter pour debug + garder top KEEP_PER_CAT
          aiSugg[cat] = scored.slice(0, KEEP_PER_CAT).map(s => {
            s.candidate._outfitCohesion = s.cohesion;
            return s.candidate;
          });
        }
        console.log(`[RefreshLook] OutfitEngine re-rank done : ${Object.keys(aiSugg).length} categories`);
      } catch (e) {
        console.warn('[RefreshLook] OutfitEngine re-rank failed:', e?.message);
      }

      // ═══ Assembler en round-robin pour diversification catégorielle ═══
      // On évite d'afficher 4 chaussures d'affilée, on alterne
      const byCat = Object.entries(aiSugg); // [['Chaussures', [...]], ['Sacs', [...]], ...]
      const roundRobin = [];
      const indexes = byCat.map(() => 0);
      let added = true;
      while (added) {
        added = false;
        for (let i = 0; i < byCat.length; i++) {
          const [, items] = byCat[i];
          if (indexes[i] < items.length) {
            roundRobin.push(items[indexes[i]]);
            indexes[i]++;
            added = true;
          }
        }
      }
      let suggs = roundRobin;

      // Fallback si pas assez (pool iQueens vide pour certaines cats)
      if (suggs.length < 8) {
        const legacy = suggestCatalogItems(pseudoOutfit, shuffled, 12);
        suggs = suggs.concat(legacy.filter(x => !suggs.some(s => s.id === x.id)));
      }
      suggs = suggs.filter(isValid);

      // ═══ MODE APPEND : ajoute toujours au moins 10 nouveaux items ═══
      // Sinon le user clique "Plus d'articles" sans rien voir arriver
      let finalSuggs;
      if (append) {
        const existingSuggs = (look.suggestions || []).filter(isValid);
        const newItems = suggs.filter(p => !existingSuggs.some(e => e.id === p.id));
        console.log(`[RefreshLook] ${look.id}: append mode → ${newItems.length} nouveaux items à ajouter`);

        // Si append génère < 5 nouveaux items → réessayer avec pool shuffle différent
        if (newItems.length < 5) {
          console.log(`[RefreshLook] not enough new items, retrying with different shuffle…`);
          const reshuffled = [...pool].sort(() => Math.random() - 0.5);
          const retry = suggestCatalogItems(pseudoOutfit, reshuffled, 20);
          const retryNew = retry.filter(p =>
            !existingSuggs.some(e => e.id === p.id) &&
            !newItems.some(n => n.id === p.id)
          );
          newItems.push(...retryNew);
          console.log(`[RefreshLook] retry added ${retryNew.length} more → total new: ${newItems.length}`);
        }

        finalSuggs = [...existingSuggs, ...newItems].slice(0, 50); // cap 50 en append
      } else {
        // Mode refresh initial : 20 items
        finalSuggs = suggs.slice(0, 20);
      }

      // Update le look en mémoire + persister
      const next = userCreatedLooks.map(l =>
        l.id === look.id ? { ...l, suggestions: finalSuggs } : l
      );
      setUserCreatedLooks(next);
      try {
        const uid = user?.id || 'guest';
        await AsyncStorage.setItem(`outfit_created_looks_${uid}`, JSON.stringify(next));
      } catch {}
      console.log(`[RefreshLook] ${look.id}: ${finalSuggs.length} suggestions (append=${append})`);

      // ═══ Si append avec succès → auto-switch en mode expand pour voir tous les items ═══
      // Sinon l'user ne voit pas forcément les nouveaux items à la fin du scroll horizontal
      if (append && finalSuggs.length > (look.suggestions || []).length) {
        setExpandedLookIds(prev => ({ ...prev, [look.id]: true }));
      }
    } catch(e) {
      console.warn('[RefreshLook] error:', e.message);
    } finally {
      setLoadingRefreshLookId(null);
    }
  }, [loadingRefreshLookId, partnerProducts, userCreatedLooks, user?.id]);

  const [showCombos, setShowCombos] = useState(false);
  const [tab,        setTab]         = useState('items'); // 'items' | 'mylooks' (v1.12.180 : suggestions retiré)

  // Migration : si un user avait 'combos' ou 'suggestions' sélectionné avant
  // que ces onglets soient supprimés, on retombe sur 'items'.
  useEffect(() => {
    if (tab === 'combos' || tab === 'suggestions') setTab('items');
  }, [tab]);

  // v1.12.284 — bascule de tab depuis HomeScreen (FAB "Voir mes looks")
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_set_tab', (key) => {
      if (key === 'items' || key === 'mylooks') setTab(key);
    });
    return () => sub.remove();
  }, []);
  // v1.12.311 — ouvre directement la modale "Ajouter un article" depuis l'extérieur
  // (bouton "Ajouter au dressing" dans CreateLookModal quand le dressing est vide).
  // Pas de délai : le Modal (overFullScreen) se rend par-dessus le tab courant.
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_open_add', () => {
      setTab('items');
      try { handleOpenAdd(); } catch {}
    });
    return () => sub.remove();
  }, [handleOpenAdd]);
  // v1.12.284 — quand un look est créé depuis HomeScreen FAB, on reload
  // v1.12.898 — force=true pour bypass le throttle 30s (le user vient de créer)
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_look_added', () => {
      loadUserCreatedLooks({ force: true });
    });
    return () => sub.remove();
  }, [loadUserCreatedLooks]);

  const filtered = useMemo(() =>
    (items || []).filter(item => {
      const matchCat = category === 'Tout' || item.category === category;
      // v1.12.89 — recherche nom IA + pseudo user (ai_metadata.pseudo)
      const q = (search || '').toLowerCase();
      const nm = (item.name || '').toLowerCase();
      const ps = (item.ai_metadata?.pseudo || '').toLowerCase();
      const matchS = !q || nm.includes(q) || ps.includes(q);
      return matchCat && matchS;
    }), [items, search, category]);

  // Max combinaisons selon le tier (5 free/basic, 10 premium, 30 gold)
  const [maxCombos, setMaxCombos] = useState(5);
  useEffect(() => {
    (async () => {
      try {
        const { getLimits } = require('../services/PremiumService');
        const limits = await getLimits(user);
        const m = limits?.looks_per_day;
        // Nombre de combos = limite d'abonnement (Free=5, Basic=5, Premium=10, Gold=∞)
        // Gold = ∞ → on génère jusqu'à 50 combos (cap pratique pour affichage)
        const computed = (m === Infinity || !isFinite(m)) ? 50 : m;
        setMaxCombos(computed);
      } catch { setMaxCombos(5); }
    })();
  }, [user?.id, user?.tier, user?.plan]);

  const combinations   = useMemo(() => generateCombinations(items, maxCombos, partnerProducts), [items, partnerProducts, maxCombos]);
  const suggestions    = useMemo(() => getMissingPieceSuggestions(items), [items]);
  const [mixedCombos,   setMixedCombos]  = useState([]);
  const [aiComboLoading, setAiComboLoading] = useState(false);

  // ═══ Affichage progressif : 5 au début, puis bouton "Voir plus" ═══
  const [visibleCombosCount, setVisibleCombosCount] = useState(5);
  // Reset le compteur à 5 quand on change d'onglet ou que les combos sont regénérés
  useEffect(() => { setVisibleCombosCount(5); }, [tab, items.length]);

  // Enrichir les combos avec Claude — vérifier limite IA à l'ouverture de l'onglet
  // Track si on vient juste d'entrer sur l'onglet (force refresh)
  const prevTabRef = React.useRef(null);

  // Quand on revient sur l'écran Dressing (via bottom nav), reset la ref
  // → la prochaine entrée sur l'onglet Suggestions forcera un refresh
  useFocusEffect(
    useCallback(() => {
      prevTabRef.current = null;
      // ═══ FIX 2026-04 : vérifier le quota IA à l'arrivée sur l'écran Dressing ═══
      // Si l'user a dépassé sa limite journalière, on affiche le popup Premium
      // immédiatement pour l'informer (mode auto=true → 1 seul popup par visite
      // de l'écran, pas de spam).
      guardCheck('ai', 0, true).then(() => {});
    }, [guardCheck])
  );

  useEffect(() => {
    if (tab !== 'suggestions') {
      prevTabRef.current = tab;
      return;
    }
    // Détection : on vient d'arriver sur 'suggestions' depuis un autre onglet
    const justEntered = prevTabRef.current !== 'suggestions';
    prevTabRef.current = tab;

    // Vérifier limite IA une fois par ouverture de l'onglet
    guardCheck('ai', 0, true).then(allowed => {
      if (!allowed) return; // popup déjà affiché par guardCheck
    });
    if (!Array.isArray(combinations) || !Array.isArray(items)) return;
    if (!combinations.length && !items.length) return;

    // ═══ CACHE CHECK : skip recompute si mêmes inputs ET pas juste entré ═══
    // → Au changement de tab vers "Suggestions", on force un refresh
    // → Sur re-renders internes, on utilise le cache
    const gender = user?.gender || 'F';
    let cacheKey = null;
    try {
      const { makeKey, getCache, invalidateNamespace } = require('../services/LooksCacheService');
      cacheKey = makeKey('wardrobe-mixed', items, partnerProducts, 20, gender, maxCombos);

      if (justEntered) {
        // Force refresh : invalider le cache du namespace
        invalidateNamespace('wardrobe-mixed');
        console.log('[WARDROBE] entered Suggestions tab → cache invalidated, regenerating');
      } else {
        const cached = getCache(cacheKey);
        if (cached) {
          console.log(`[WARDROBE] ✓ cache hit mixedCombos (${cached.length} combos)`);
          setMixedCombos(cached);
          return;
        }
      }
    } catch {}

    const base = buildMixedCombos(combinations, partnerProducts, items, gender);
    console.log(`[WARDROBE] mixedCombos built: ${base.length} tenues (partnerProducts=${partnerProducts.length})`);
    if (base.length > 0) {
      base.forEach((c, i) => console.log(`  Combo ${i+1} "${c.label}": ${c.items.length} articles (${c.items.filter(it=>!it.owned).length} à acheter, prix=${c.price}€)`));
    }
    setMixedCombos(base); // Afficher immédiatement

    // Stocker la base dans le cache (enrichissement IA vient après mais pas bloquant)
    try {
      if (cacheKey) {
        const { setCache } = require('../services/LooksCacheService');
        setCache(cacheKey, base);
      }
    } catch {}

    if (!base.length) return;

    setAiComboLoading(true);
    const enrichCombosWithAI = async () => {
      try {
        // v1.12.105 — Quota AI retiré (n'incrémente que pour Claude Vision sur
        // un nouvel article). Ajout cache AsyncStorage 24h pour réduire les
        // appels Claude répétés (combos identiques sur reload/changement onglet).
        const comboSig = base.map(c => `${c.label}:${c.items.map(it => it.id || it.name).join(',')}`).join('|');
        const cacheKey = `outfit_combos_aitips_${comboSig.slice(0, 200)}`;
        try {
          const cached = await AsyncStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && Date.now() - (parsed.ts || 0) < 24 * 3600 * 1000 && Array.isArray(parsed.tips)) {
              const enrichedFromCache = base.map((combo, i) => ({ ...combo, aiTip: parsed.tips[i] || '' }));
              setMixedCombos(enrichedFromCache);
              setAiComboLoading(false);
              return;
            }
          }
        } catch {}

        // Préparer le contexte dressing pour Claude
        const dressingDesc = items.slice(0, 12).map(i =>
          `${i.name} (${i.category}, ${i.color})`
        ).join(', ');

        const combosDesc = base.map((combo, i) =>
          "Combo " + (i+1) + " \"" + combo.label + "\": " + combo.items.map(it =>
            it.name + "(" + (it.owned ? "possede" : "a acheter " + it.price + "EUR") + ")"
          ).join(' + ')
        ).join(" | ");

        const res = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: 'claude-haiku-4-5-20251001',
            max_tokens: 500,
            messages: [{ role: 'user', content:
              `Styliste mode expert. Dressing: ${dressingDesc}.\n\nCombos proposés:\n${combosDesc}\n\nPour chaque combo, donne UN conseil de style ultra-court (max 10 mots) qui justifie pourquoi les articles s'accordent bien et met en valeur l'article à acheter. Format: juste le conseil, numéroté 1. 2. 3. etc.`
            }]
          })
        });

        const data = await res.json();
        const rawTips = (data.content?.[0]?.text || '').split('\n')
          .filter(l => l.match(/^\d+\.?\s/))
          .map(l => l.replace(/^\d+\.?\s*/, '').trim());

        // Appliquer les tips Claude + améliorer le scoring des articles catalogue
        const enriched = base.map((combo, i) => {
          const aiTip = rawTips[i] || '';

          // Re-scorer les articles non possédés avec les couleurs du dressing
          const ownedColors = combo.items
            .filter(it => it.owned)
            .map(it => (it.color || '').toLowerCase());

          const neutrals = ['noir','blanc','beige','gris','camel','nude','navy','ivoire'];
          const betterItems = combo.items.map(it => {
            if (it.owned) return it;
            const color = (it.color || '').toLowerCase();
            const isNeutral = neutrals.some(n => color.includes(n));
            const matches = ownedColors.some(oc => oc === color || neutrals.some(n => oc.includes(n)));
            return {
              ...it,
              matchScore: isNeutral ? 10 : matches ? 8 : 5,
            };
          });

          return { ...combo, items: betterItems, aiTip };
        });

        // Cache 24h des tips
        try {
          await AsyncStorage.setItem(cacheKey, JSON.stringify({
            ts: Date.now(),
            tips: enriched.map(c => c.aiTip || ''),
          }));
        } catch {}

        setMixedCombos(enriched);
      } catch(e) {
        console.warn('[COMBOS] AI enrichment error:', e.message);
      } finally {
        setAiComboLoading(false);
      }
    };

    enrichCombosWithAI();
  }, [tab, combinations?.length, partnerProducts?.length, items?.length, maxCombos]);
  // partnerProducts / loadingPartners / loadingMoreCombos : déclarations
  // remontées plus haut (juste après userCreatedLooks) pour éviter TDZ web.
  const displayedComboSigsRef = React.useRef(new Set()); // signatures des combos déjà affichés

  // Charger plus d'articles partenaires pour enrichir les combinaisons
  const handleLoadMoreCombos = React.useCallback(async () => {
    if (loadingMoreCombos) return;
    setLoadingMoreCombos(true);
    try {
      const gender = user?.gender || 'F';

      // Mémoriser les signatures des combos déjà affichés
      for (const c of (mixedCombos || [])) {
        if (c.signature) displayedComboSigsRef.current.add(c.signature);
      }

      // Détecter les catégories manquantes dans le dressing pour cibler le fetch
      const ownedCats = new Set((items || []).map(i => i.category));
      const MISSING_CATS = ['Chaussures', 'Sacs', 'Bijoux', 'Vestes', 'Manteaux', 'Accessoires', 'Hauts', 'Bas', 'Robes']
        .filter(c => !ownedCats.has(c) || (items || []).filter(i => i.category === c).length < 3);

      console.log('[Wardrobe] loadMore: missing cats=', MISSING_CATS.slice(0, 4).join(','));

      // Fetch ciblé sur catégories manquantes + variations
      const fetchPromises = [
        getZalandoProducts(gender, 'Tout', 30).catch(() => []),
        getAsosProducts(gender, 'Tout', 30).catch(() => []),
        getHMProducts(gender, 'Tout', 20).catch(() => []),
        getZaraProducts(gender, 'Tout').catch(() => []),
      ];
      // Ajouter 2-3 fetchs ciblés sur les catégories manquantes
      for (const cat of MISSING_CATS.slice(0, 4)) {
        fetchPromises.push(getZalandoProducts(gender, cat, 15).catch(() => []));
        fetchPromises.push(getAsosProducts(gender, cat, 15).catch(() => []));
      }

      const fetched = await Promise.all(fetchPromises);
      const incoming = fetched.flat()
        .filter(p => (p.image || p.image_url) && (p.link || p.affiliateUrl || p.url) && (p.price || 0) > 0);

      // Déduper vs partnerProducts existants
      const seen = new Set(partnerProducts.map(p => p.id));
      const newOnes = incoming.filter(p => p.id && !seen.has(p.id));
      console.log(`[Wardrobe] loadMore: +${newOnes.length} nouveaux articles partenaires`);

      const nextPartners = newOnes.length > 0 ? [...partnerProducts, ...newOnes] : partnerProducts;
      if (newOnes.length > 0) setPartnerProducts(nextPartners);

      // Re-build des combos en excluant ceux déjà vus + avec pool étendu
      const newCombos = buildMixedCombos(
        combinations,
        nextPartners,
        items,
        gender,
        displayedComboSigsRef.current
      );

      if (newCombos.length === 0) {
        // Cas limite : tous les styles épuisés → reset le tracker et regénérer
        console.log('[Wardrobe] loadMore: tous styles épuisés, reset tracker');
        displayedComboSigsRef.current.clear();
        const freshCombos = buildMixedCombos(combinations, nextPartners, items, gender, new Set());
        setMixedCombos(freshCombos.slice(0, 50)); // cap 50
      } else {
        // Ajouter les NOUVEAUX combos à la suite des anciens, cap total 50
        setMixedCombos(prev => {
          const existingSigs = new Set((prev || []).map(c => c.signature).filter(Boolean));
          const trulyNew = newCombos.filter(c => !existingSigs.has(c.signature));
          return [...(prev || []), ...trulyNew].slice(0, 50); // cap 50
        });
      }
    } catch(e) {
      console.warn('[Wardrobe] loadMore error:', e.message);
    } finally {
      setLoadingMoreCombos(false);
    }
  }, [loadingMoreCombos, partnerProducts, combinations, items, mixedCombos, user?.gender, maxCombos]);

  // usePremiumGuard remonté plus haut (ligne ~498) — voir commentaire FIX TDZ web

  // ═══ FIX 2026-04 : listeners events globaux (quota IA + upgrade tier) ═══
  // - ai_limit_reached : émis par AIStylistService.guardAIQuota() quand un
  //   appel IA est bloqué par le quota. Déclenche le popup Premium.
  // - tier_changed : émis par PremiumService.saveTierLocally() quand l'user
  //   upgrade (ex: Free → Basic). Ferme le popup s'il était ouvert, car le
  //   nouveau tier a probablement débloqué la limite.
  React.useEffect(() => {
    // v1.13.88 — SUPPRESSION du listener ai_limit_reached DUPLIQUÉ ici : il
    //   appelait guardCheck('ai') à 300ms SANS attendre la fin des interactions,
    //   pendant que le WardrobeAddProgressModal se fermait → le paywall
    //   s'affichait puis disparaissait aussitôt (conflit 2-Modals / flash).
    //   usePremiumGuard écoute DÉJÀ ai_limit_reached et affiche le paywall
    //   proprement (InteractionManager + 350ms) → source UNIQUE.
    const tierSub = DeviceEventEmitter.addListener('tier_changed', (payload) => {
      console.log(`[Wardrobe] 🎉 tier changed to ${payload?.tier} → closing upgrade popup`);
      setShowPremium(false);
    });
    return () => {
      tierSub.remove();
    };
  }, [setShowPremium]);

  // ═══ FIX 2026-04 : popup DÉSACTIVÉE sur affichage automatique ═══
  // Avant : à chaque fois qu'on arrivait sur Dressing, si items.length >= max,
  // on affichait la popup "Dressing plein". Problème : racing condition avec
  // le cache local (items résiduel d'un ancien compte, données en flux, etc.)
  // → popup s'affichait même quand le dressing paraît vide.
  //
  // Maintenant : la popup ne s'affiche QUE lors d'une action utilisateur
  // (clic sur "+" d'ajout) via guardCheck('wardrobe', ..., false). À ce
  // moment-là, items est stable et le check est fiable.
  //
  // Effet désactivé mais laissé commenté en cas de revert nécessaire :
  //
  // useEffect(() => {
  //   const len = (items||[]).length;
  //   if (len === 0) return;
  //   (async () => {
  //     try {
  //       const { getLimits } = require('../services/PremiumService');
  //       const limits = await getLimits(user);
  //       const max = limits?.wardrobe_items;
  //       if (typeof max === 'number' && isFinite(max) && max > 0 && len >= max) {
  //         guardCheck('wardrobe', len, true);
  //       }
  //     } catch {}
  //   })();
  // }, [(items||[]).length, user?.id]);

  // Charger produits partenaires — dès le montage, pas seulement sur onglet combos
  // → pour avoir les suggestions dispo partout (CreateLookModal, Suggestions tab, etc.)
  React.useEffect(() => {
    if (partnerProducts.length > 0) return;
    const gender = user?.gender || 'F';
    const cacheKey = `outfit_partners_cache_v12_${gender}`;

    (async () => {
      // ═══ ÉTAPE 1 : CACHE INSTANTANÉ ═══
      // Afficher immédiatement le dernier cache (si moins de 6h)
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          const { data, ts } = JSON.parse(cached);
          const age = Date.now() - ts;
          if (Array.isArray(data) && data.length > 50 && age < 6 * 3600 * 1000) {
            // Filtre genre strict sur le cache aussi (au cas où cache créé avant maj)
            let cachedFiltered = data;
            try {
              const GF = require('../services/GenderFilter');
              if (GF && GF.filterByGender) cachedFiltered = GF.filterByGender(data, gender);
            } catch {}
            console.log(`[WARDROBE] cache hit: ${cachedFiltered.length}/${data.length} products (age ${Math.round(age/60000)}min)`);
            setPartnerProducts(cachedFiltered);
          }
        }
      } catch {}

      setLoadingPartners(true);

      // ═══ ÉTAPE 2 : FETCH PARALLÈLE + STREAMING ═══
      const { fetchDynamicCatalog } = await import('../services/DynamicCatalogService');

      const dynamicPromise = fetchDynamicCatalog(gender).catch(() => []);
      const partnersPromise = Promise.all([
        // ═══ Fetchs généraux "Tout" ═══
        getZalandoProducts(gender, 'Tout', 20).catch(() => []),
        getZaraProducts(gender, 'Tout').catch(() => []),
        getAsosProducts(gender, 'Tout', 15).catch(() => []),
        getHMProducts(gender, 'Tout', 15).catch(() => []),
        // ═══ Fetchs dédiés par catégorie vêtements principale ═══
        getZalandoProducts(gender, 'Hauts', 20).catch(() => []),
        getAsosProducts(gender, 'Hauts', 15).catch(() => []),
        getHMProducts(gender, 'Hauts', 15).catch(() => []),
        getZalandoProducts(gender, 'Bas', 15).catch(() => []),
        getAsosProducts(gender, 'Bas', 15).catch(() => []),
        ...(gender === 'F' ? [
          getZalandoProducts(gender, 'Robes', 15).catch(() => []),
          getAsosProducts(gender, 'Robes', 15).catch(() => []),
        ] : []),
        getZalandoProducts(gender, 'Vestes', 10).catch(() => []),
        // ═══ Accessoires ═══
        getZalandoProducts(gender, 'Bijoux', 10).catch(() => []),
        getAsosProducts(gender, 'Bijoux', 10).catch(() => []),
        getZalandoProducts(gender, 'Chaussures', 15).catch(() => []),
        getAsosProducts(gender, 'Chaussures', 10).catch(() => []),
        getZalandoProducts(gender, 'Sacs', 10).catch(() => []),
      ]);

      // Dès que le dynamic arrive (le plus gros), on display
      try {
        const dynamic = await dynamicPromise || [];
        if (dynamic.length > 0) {
          // Filtre genre strict sur dynamic avant display
          let dynFiltered = dynamic;
          try {
            const GF = require('../services/GenderFilter');
            if (GF && GF.filterByGender) dynFiltered = GF.filterByGender(dynamic, gender);
          } catch {}
          console.log(`[WARDROBE] dynamic first: ${dynFiltered.length}/${dynamic.length} products`);

          // Enrichissement tendances (asynchrone en background)
          try {
            const { getTrendingProducts } = require('../services/TrendingService');
            const { products: enriched } = await getTrendingProducts(dynFiltered, gender);
            dynFiltered = enriched;
          } catch(e) {}

          setPartnerProducts(prev => {
            const merged = dynFiltered;
            const seen = new Set();
            return merged.filter(p => {
              const key = p.id || `${p.partner}_${p.name}_${p.price}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          });
        }
      } catch {}

      // Puis on enrichit avec les autres partenaires
      try {
        const [dynamic, partnersArrays] = await Promise.all([
          dynamicPromise,
          partnersPromise,
        ]);
        // Flatten tous les résultats partenaires (array d'arrays)
        const partnersFlat = Array.isArray(partnersArrays) ? partnersArrays.flat() : [];
        const all = [...(dynamic || []), ...partnersFlat];

        const seen = new Set();
        const unique = all.filter(p => {
          const key = p.id || `${p.partner}_${p.name}_${p.price}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // ═══ FILTRE GENRE STRICT (Hauts agressif) ═══
        let finalProducts = unique;
        try {
          const GF = require('../services/GenderFilter');
          if (GF && GF.filterHautsByGenderStrict) {
            finalProducts = GF.filterHautsByGenderStrict(unique, gender);
            if (finalProducts.length !== unique.length) {
              console.log(`[WARDROBE] gender filter (Hauts strict): ${finalProducts.length}/${unique.length}`);
            }
          }
          // ═══ FILTRE GENRE GLOBAL (toutes catégories) ═══
          if (GF && GF.filterByGender && gender && ['M', 'F'].includes(gender)) {
            const before = finalProducts.length;
            finalProducts = GF.filterByGender(finalProducts, gender);
            if (finalProducts.length !== before) {
              console.log(`[WARDROBE] gender filter (all cats): ${finalProducts.length}/${before} (target=${gender})`);
            }
          }
        } catch(e) {}

        console.log(`[WARDROBE] partnerProducts complete: ${finalProducts.length}`);

        // ═══ FIX 2026-04 : enrichir AVANT le trending ═══
        // Sans enrichCatalogWithAI, aucun produit n'a ai_metadata → TrendingService
        // ne trouve rien (même si les produits sont en DB catalog_analysis).
        // On enrichit depuis /catalog-analysis (backend) + cache AsyncStorage local.
        try {
          const { enrichCatalogWithAI } = require('../services/AIStylistService');
          let token = null;
          try { token = await getValidToken(); } catch {}
          finalProducts = await enrichCatalogWithAI(finalProducts, token);
        } catch(e) {
          console.warn('[WARDROBE] enrichCatalogWithAI failed:', e.message);
        }

        // ═══ ENRICHISSEMENT TENDANCES IA ═══
        try {
          const { getTrendingProducts } = require('../services/TrendingService');
          const { products: enriched, trendingCount } = await getTrendingProducts(finalProducts, gender);
          console.log(`[WARDROBE] ${trendingCount} produits tendance détectés`);
          finalProducts = enriched;
        } catch(e) {
          console.warn('[WARDROBE] trending failed:', e.message);
        }

        setPartnerProducts(finalProducts);

        // ═══ Prefetch 30 premières images pour fluidifier le scroll ═══
        try {
          const { prefetchImages } = require('../services/ImageCacheService');
          const uris = finalProducts.slice(0, 30).map(p => p.image).filter(Boolean);
          if (uris.length > 0) prefetchImages(uris, { priority: 'low' });
        } catch {}

        // Sauvegarder en cache pour le prochain démarrage instantané
        try {
          await AsyncStorage.setItem(cacheKey, JSON.stringify({ data: finalProducts, ts: Date.now() }));
        } catch {}
      } catch(e) {
        console.warn('[WARDROBE] loadPartners:', e.message);
      } finally {
        setLoadingPartners(false);
      }
    })();
  }, [user?.gender]);

  const onAddItem = async (newItem) => {
    // Vérifier la limite dressing avant d'ajouter
    const ok = await guardCheck('wardrobe', (items||[]).length, false);
    if (!ok) return;
    setAddProgressError(null);
    // Afficher l'image locale immédiatement (en haut de liste, plus récent first)
    const localImage = newItem.image || null;
    const tempItem = {
      ...newItem,
      id: 'w' + Date.now(),
      image: localImage,
      image_url: localImage,
      created_at: new Date().toISOString(),
    };
    setItems(prev => [tempItem, ...prev]);
    // v1.13.84 — FIX "popup de progression ne s'affiche pas / statuts ne changent
    //   pas" : on FERME d'abord l'AddItemModal, on ATTEND sa fermeture, PUIS on
    //   ouvre le popup de progression. Sur Android, 2 <Modal> qui se chevauchent
    //   → le 2ᵉ ne s'affiche pas / ne se met plus à jour (le popup restait figé).
    setShowAdd(false);
    await new Promise(r => setTimeout(r, Platform.OS === 'android' ? 320 : 30));
    // v1.12.119 — popup de progression (état: uploading)
    setAddProgress('uploading');

    let finalImageUrl = localImage;

    // ═══ Web : si l'URL est un blob:// ou data:URI, on upload via fetch.blob ═══
    // (file picker browser → URL.createObjectURL → blob:https://... non valide
    // pour le backend qui doit lire l'image). On lit le blob, on PUT sur S3 via
    // presigned URL, puis on garde la publicUrl HTTPS dans finalImageUrl.
    const isWebBlob = Platform.OS === 'web' && localImage &&
      (localImage.startsWith('blob:') || localImage.startsWith('data:'));
    if (isWebBlob) {
      try {
        console.log(`[Wardrobe-Upload web] 📤 Démarrage upload blob URL`);
        const token = await getValidToken();
        if (!token) throw new Error('no token');

        // 1. Lire le blob (via fetch sur l'URL blob locale)
        const blobRes = await fetch(localImage);
        if (!blobRes.ok) throw new Error(`fetch blob failed: ${blobRes.status}`);
        const blob = await blobRes.blob();
        const contentType = blob.type || 'image/jpeg';
        console.log(`[Wardrobe-Upload web] ✓ blob lu (${blob.size} bytes, ${contentType})`);

        // 2. Obtenir URL pré-signée
        const presignRes = await fetch(AWS_CONFIG.apiUrl + '/wardrobe/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ file_type: contentType, source: 'web' }),
        });
        if (!presignRes.ok) {
          const errBody = await presignRes.text().catch(() => '');
          throw new Error(`presign ${presignRes.status}: ${errBody.slice(0, 200)}`);
        }
        const { uploadUrl, publicUrl } = await presignRes.json();
        if (!uploadUrl || !publicUrl) throw new Error('presign returned invalid data');
        console.log(`[Wardrobe-Upload web] ✓ presign OK`);

        // 3. PUT le blob direct sur S3
        const s3Res = await fetch(uploadUrl, {
          method:  'PUT',
          headers: { 'Content-Type': contentType },
          body:    blob,
        });
        if (!s3Res.ok) {
          const errBody = await s3Res.text().catch(() => '');
          throw new Error(`s3 ${s3Res.status}: ${errBody.slice(0, 200)}`);
        }
        finalImageUrl = publicUrl;
        console.log(`[Wardrobe-Upload web] ✅ Upload OK: ${publicUrl}`);

        // Mettre à jour l'item local avec l'URL S3
        setItems(prev => prev.map(it =>
          it.id === tempItem.id ? { ...it, image: publicUrl, image_url: publicUrl } : it
        ));
      } catch (e) {
        console.error(`[Wardrobe-Upload web] ❌ Upload FAILED: ${e.message}`);
        setItems(prev => prev.filter(it => it.id !== tempItem.id));
        setAddProgressError(`Impossible d'enregistrer la photo. ${e.message}`);
        setAddProgress('error');
        return;
      }
    }

    // Upload photo sur S3 si on a un URI local (photoB64 pas obligatoire)
    // FileSystem.uploadAsync peut lire directement depuis l'URI.
    const hasLocalUri = localImage && localImage.startsWith('file://');
    if (hasLocalUri) {
      try {
        console.log(`[Wardrobe-Upload] 📤 Démarrage upload, URI=${localImage.slice(0, 60)}...`);
        const token = await getValidToken();
        if (!token) throw new Error('no token');
        console.log(`[Wardrobe-Upload] 🔑 token ok (${token.length} chars)`);

        // 1. Obtenir URL pré-signée
        console.log(`[Wardrobe-Upload] 🌐 1/3 POST /wardrobe/upload-url`);
        const presignRes = await fetch(AWS_CONFIG.apiUrl + '/wardrobe/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ file_type: 'image/jpeg', source: 'camera' }),
        });
        if (!presignRes.ok) {
          const errBody = await presignRes.text().catch(() => '');
          throw new Error(`presign ${presignRes.status}: ${errBody.slice(0, 200)}`);
        }
        const presignData = await presignRes.json();
        const { uploadUrl, publicUrl } = presignData;
        console.log(`[Wardrobe-Upload] ✓ 1/3 presign OK → uploadUrl=${(uploadUrl||'').slice(0, 80)}... publicUrl=${(publicUrl||'').slice(0, 80)}`);

        if (!uploadUrl || !publicUrl) {
          throw new Error(`presign returned invalid data: ${JSON.stringify(presignData).slice(0, 200)}`);
        }

        // 2. Upload sur S3 via expo-file-system (plus fiable que Blob PUT en RN)
        // Raison : React Native iOS a un bug connu où fetch(..., { body: blob })
        // ne transmet parfois pas bien le body, d'où des PUT vides ou des erreurs
        // SSL. FileSystem.uploadAsync() utilise l'API native iOS/Android et
        // fonctionne pour les uploads S3 presigned.
        //
        // ═══ FIX SDK 54 : import depuis /legacy ═══
        // Depuis Expo SDK 54, l'API uploadAsync/readAsStringAsync/getInfoAsync
        // est dépréciée dans 'expo-file-system' mais toujours dispo sous
        // 'expo-file-system/legacy'. On tente les 2 imports pour compat
        // ascendante (ancien SDK: top-level works / nouveau SDK: legacy).
        console.log(`[Wardrobe-Upload] 🌐 2/3 upload via FileSystem.uploadAsync (image URI: ${localImage.slice(0, 80)})`);
        let FileSystem;
        try {
          // Essayer d'abord le chemin legacy (SDK 54+)
          FileSystem = require('expo-file-system/legacy');
          if (!FileSystem.uploadAsync) {
            // Si le module legacy n'a pas uploadAsync, fallback
            FileSystem = require('expo-file-system');
          }
          console.log(`[Wardrobe-Upload] 🔧 FileSystem loaded, uploadAsync=${typeof FileSystem.uploadAsync} readAsStringAsync=${typeof FileSystem.readAsStringAsync}`);
        } catch (importErr) {
          console.log(`[Wardrobe-Upload] ⚠️ legacy import failed (${importErr.message}), using default`);
          FileSystem = require('expo-file-system');
        }

        // Vérifier que le fichier existe bien avant d'upload
        try {
          if (typeof FileSystem.getInfoAsync === 'function') {
            const info = await FileSystem.getInfoAsync(localImage);
            console.log(`[Wardrobe-Upload] 📁 file info: exists=${info.exists} size=${info.size}B`);
            if (!info.exists) {
              throw new Error(`file does not exist: ${localImage}`);
            }
          }
        } catch (infoErr) {
          console.warn(`[Wardrobe-Upload] ⚠️ getInfoAsync failed: ${infoErr.message}`);
        }

        let s3Status, s3Body;

        // ═══ FIX 2026-04 : stratégie upload en binaire pur ═══
        // Le problème : FileSystem.uploadAsync avec uploadType=1 (BINARY_CONTENT)
        // dans certaines versions SDK 54 legacy encode en multipart/form-data
        // au lieu de body binaire brut. S3 accepte (200 OK) mais stocke le
        // bloc multipart → Claude voit "--F808425..." au lieu du JPEG.
        //
        // Solution : on lit le fichier en base64, on décode en Uint8Array,
        // on fait un fetch PUT avec le Uint8Array en body. C'est du binaire
        // pur, S3 stocke directement les octets du JPEG.

        // 1. Lire le base64
        let b64 = newItem.photoB64;
        if (!b64) {
          console.log('[Wardrobe-Upload] 📖 Lecture base64 depuis URI...');
          try {
            const encoding = (FileSystem.EncodingType && FileSystem.EncodingType.Base64) || 'base64';
            b64 = await FileSystem.readAsStringAsync(localImage, { encoding });
            console.log(`[Wardrobe-Upload] ✓ base64 lu (${b64.length} chars)`);
          } catch (readErr) {
            throw new Error(`Could not read image as base64: ${readErr.message}`);
          }
        }

        // 2. Décoder en bytes bruts
        // v1.13.84 — céder le thread JS AVANT l'atob + la boucle (qui bloquent sur
        //   une grosse image) pour que le popup de progression peigne son état.
        await new Promise(r => setTimeout(r, 0));
        const byteChars = atob(b64);
        const byteArr   = new Uint8Array(byteChars.length);
        for (var i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);

        // 3. Vérifier les magic bytes avant d'envoyer (diagnostic)
        const firstBytes = Array.from(byteArr.slice(0, 4))
          .map(b => b.toString(16).padStart(2, '0')).join(' ');
        console.log(`[Wardrobe-Upload] 🔍 magic bytes: ${firstBytes} (${byteArr.length} bytes total)`);
        const isValidJpeg = byteArr[0] === 0xFF && byteArr[1] === 0xD8;
        const isValidPng  = byteArr[0] === 0x89 && byteArr[1] === 0x50;
        if (!isValidJpeg && !isValidPng) {
          console.warn(`[Wardrobe-Upload] ⚠️ magic bytes non-JPEG/PNG : ${firstBytes}`);
        }

        // 4. Upload via fetch PUT avec Uint8Array en body (binaire pur)
        console.log(`[Wardrobe-Upload] 🌐 2/3 PUT binaire pur (Uint8Array body)...`);
        try {
          const s3Res = await fetch(uploadUrl, {
            method:  'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body:    byteArr, // Uint8Array = binaire pur, pas de multipart
          });
          s3Status = s3Res.status;
          s3Body   = await s3Res.text().catch(() => '');
          console.log(`[Wardrobe-Upload] ✓ 2/3 PUT Uint8Array status=${s3Status}`);
        } catch (putErr) {
          // Fallback : certains RN versions gèrent mal Uint8Array direct en body
          // → on essaie avec Blob
          console.warn(`[Wardrobe-Upload] ⚠️ PUT Uint8Array failed (${putErr.message}), tentative Blob`);
          const blob = new Blob([byteArr], { type: 'image/jpeg' });
          const s3Res2 = await fetch(uploadUrl, {
            method:  'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body:    blob,
          });
          s3Status = s3Res2.status;
          s3Body   = await s3Res2.text().catch(() => '');
          console.log(`[Wardrobe-Upload] ✓ 2/3 PUT Blob fallback status=${s3Status}`);
        }
        if (s3Status < 200 || s3Status >= 300) {
          throw new Error(`s3 ${s3Status}: ${s3Body.slice(0, 200)}`);
        }

        finalImageUrl = publicUrl;
        console.log(`[Wardrobe-Upload] ✅ S3 upload successful: ${publicUrl}`);

        // 3. Mettre à jour l'item local avec l'URL S3
        setItems(prev => prev.map(function(it) {
          return it.id === tempItem.id ? { ...it, image: publicUrl, image_url: publicUrl } : it;
        }));
      } catch (e) {
        console.error(`[Wardrobe-Upload] ❌ S3 upload FAILED: ${e.message}`);
        // ═══ FIX 2026-04 : si upload S3 échoue, on ANNULE l'ajout ═══
        // Sans URL publique, le backend /analyze-item ne peut pas fetcher
        // l'image → il fallback sur inferMetadataFromText → sans check
        // isClothing → des photos random atterrissent dans le dressing.
        // On retire l'item provisoire et on affiche un message à l'user.
        setItems(prev => prev.filter(it => it.id !== tempItem.id));
        setAddProgressError(`Impossible d'enregistrer la photo. ${e.message}`);
        setAddProgress('error');
        return;
      }
    }

    // ═══ FIX 2026-04 : garde-fou supplémentaire ═══
    // Même si le flow précédent a réussi, on vérifiez que finalImageUrl est
    // bien une URL HTTP. Sinon on refuse (cas où newItem arrive sans photoB64
    // par ex. via un flux alternatif).
    if (!finalImageUrl || !/^https?:\/\//.test(finalImageUrl)) {
      console.warn(`[Wardrobe] ⛔ URL image non valide : ${finalImageUrl}`);
      setItems(prev => prev.filter(it => it.id !== tempItem.id));
      setAddProgressError("La photo n'a pas pu être enregistrée. Réessayez avec une autre photo.");
      setAddProgress('error');
      return;
    }

    // ═══ FIX 2026-04 : inverser le flux ═══
    // Avant : on sauvait en DB, puis analysait, puis rollback si rejet.
    //        → items visibles dans dressing qques sec, flicker désagréable.
    // Maintenant : on analyse d'abord, on sauvegarde SEULEMENT si accepté.
    //        → rien n'apparait dans le dressing tant que l'analyse n'est pas OK.
    //
    // Exception : si l'analyse IA échoue (pas de token, réseau, etc.), on
    // sauvegarde quand même sans metadata — l'user ne perd pas son article.
    if (user) {
      try {
        const token = await getValidToken();
        let metadata = null;

        // 1. Analyser AVANT sauvegarde DB (pour bloquer les photos non-vêtements)
        if (finalImageUrl && finalImageUrl.startsWith('http')) {
          try {
            console.log('[Wardrobe] 🔎 analyse préalable avant sauvegarde DB...');
            // v1.12.119 — popup passe en phase "analyzing"
            setAddProgress('analyzing');
            metadata = await analyzeItemImage({
              id:       tempItem.id,
              name:     tempItem.name,
              category: tempItem.category,
              image:    finalImageUrl,
            }, token, user);
          } catch(e) {
            console.warn('[Wardrobe] analyse préalable erreur:', e.message);
          }
        }

        // 2. Quota IA dépassé : on BLOQUE l'ajout (popup upgrade s'affichera)
        //    Avant : on ajoutait sans analyse. Maintenant : bloqué, user doit upgrade.
        if (metadata && metadata._quotaExceeded) {
          console.log(`[Wardrobe] ⛔ quota IA dépassé pour ${tempItem.name}, ajout annulé`);
          // Retirer l'item temporaire qui était affiché localement
          setItems(prev => prev.filter(it => it.id !== tempItem.id));
          // Le popup Premium s'affichera via l'event AI_LIMIT_EVENT émis par guardAIQuota
          // Fermer le popup de progression : le popup Premium prend le relais
          setAddProgress(null);
          return;
        }

        // ═══ FIX 2026-04 : bloquer aussi si analyse silencieusement échouée ═══
        // Si metadata est null (timeout, erreur réseau, clé API manquante,
        // image invalide, etc.), on n'ajoute pas l'article au dressing.
        // L'user est invité à réessayer.
        // Note : si _rejected → géré au step 3 ci-dessous (alert spécifique)
        // Note : si _quotaExceeded → géré au step 2 ci-dessus
        if (!metadata) {
          console.warn(`[Wardrobe] ⛔ analyse IA indisponible pour ${tempItem.name}, ajout annulé`);
          setItems(prev => prev.filter(it => it.id !== tempItem.id));
          setAddProgressError("L'analyse IA n'a pas pu aboutir (réseau lent ou serveur surchargé). Réessayez dans quelques instants.");
          setAddProgress('error');
          return;
        }

        // 3. Photo rejetée par isClothing=false : on N'AJOUTE PAS au dressing
        if (metadata && metadata._rejected) {
          console.log(`[Wardrobe] 🚫 photo rejetée pour "${tempItem.name}": pas de vêtement détecté`);
          // Retirer l'item temporaire (il était affiché localement)
          setItems(prev => prev.filter(it => it.id !== tempItem.id));
          setAddProgressError(metadata._userMessage || "L'IA n'a pas détecté de vêtement sur cette photo. Veuillez choisir une photo d'un vêtement, chaussure, sac ou accessoire mode.");
          setAddProgress('error');
          return;
        }

        // 4. Nom détecté par l'IA
        // v1.12.89 : le user ne saisit plus de nom — il est toujours détecté
        //   automatiquement à partir de la photo. Si _suggestedName existe,
        //   on l'utilise directement (sans popup). Si l'user avait un nom
        //   (cas legacy ou source URL), on garde la logique de prompt.
        const userProvidedName = !!(tempItem.name && tempItem.name.trim());
        if (metadata && metadata._suggestedName && !userProvidedName) {
          // Pas de nom saisi : on prend silencieusement la suggestion IA
          console.log(`[Wardrobe] ✨ nom auto-détecté par l'IA : "${metadata._suggestedName}"`);
          tempItem.name = metadata._suggestedName;
          setItems(prev => prev.map(it =>
            it.id === tempItem.id ? { ...it, name: metadata._suggestedName } : it
          ));
        } else if (metadata && metadata._nameMismatch && metadata._suggestedName) {
          console.log(
            `[Wardrobe] 📝 nom "${tempItem.name}" ne correspond pas — ` +
            `détecté="${metadata._detectedType}" → suggestion="${metadata._suggestedName}"`
          );
          // Utiliser une Promise pour attendre le choix user avant de continuer
          // showAppAlert (au lieu de Alert.alert) → marche sur web ET natif
          const finalName = await new Promise((resolve) => {
            showAppAlert(
              'Le nom ne correspond pas',
              `Vous avez saisi "${tempItem.name}" mais l'IA a détecté ${metadata._detectedType ? `"${metadata._detectedType}"` : 'autre chose'} sur la photo.\n\nQue souhaitez-vous faire ?`,
              [
                {
                  text: `Utiliser "${metadata._suggestedName}"`,
                  style: 'default',
                  onPress: () => resolve(metadata._suggestedName),
                },
                {
                  text: `Garder "${tempItem.name}"`,
                  style: 'cancel',
                  onPress: () => resolve(tempItem.name),
                },
              ],
            );
          });
          if (finalName !== tempItem.name) {
            console.log(`[Wardrobe] ✅ renommé en "${finalName}" avant sauvegarde`);
            tempItem.name = finalName;
            // Mettre à jour l'item affiché localement
            setItems(prev => prev.map(it =>
              it.id === tempItem.id ? { ...it, name: finalName } : it
            ));
          }
        }

        // v1.12.524 — Proposer la correction de catégorie si l'IA détecte un autre type
        // (ex: user="Manteau", IA="Vestes" → propose de corriger).
        if (metadata && metadata._categoryMismatch && metadata._detectedCategory) {
          console.log(
            `[Wardrobe] 📦 catégorie "${metadata._userCategory || tempItem.category}" ne correspond pas — ` +
            `détecté="${metadata._detectedCategory}"`
          );
          const userCat = metadata._userCategory || tempItem.category;
          const aiCat = metadata._detectedCategory;
          // v1.12.526 — Traduire le label de catégorie via les clés i18n existantes
          const CAT_KEY = {
            Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses',
            Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
            Sacs: 'cat_bags', Accessoires: 'cat_accessories', Bijoux: 'cat_jewelry',
            Maillots: 'cat_swimwear',
          };
          const tr = (c) => (CAT_KEY[c] && t(CAT_KEY[c])) || c;
          const userCatLabel = tr(userCat);
          const aiCatLabel = tr(aiCat);
          const finalCategory = await new Promise((resolve) => {
            showAppAlert(
              t('category_confirm_title') || 'Catégorie à confirmer',
              (t('category_confirm_msg') || 'Vous avez choisi la catégorie "{userCategory}" mais l\'IA a détecté un article de catégorie "{detectedCategory}" sur la photo.\n\nQue souhaitez-vous faire ?')
                .replace('{userCategory}', userCatLabel)
                .replace('{detectedCategory}', aiCatLabel),
              [
                {
                  text: (t('use_category_btn') || 'Utiliser "{name}"').replace('{name}', aiCatLabel),
                  style: 'default',
                  onPress: () => resolve(aiCat),
                },
                {
                  text: (t('keep_category_btn') || 'Garder "{name}"').replace('{name}', userCatLabel),
                  style: 'cancel',
                  onPress: () => resolve(userCat),
                },
              ],
            );
          });
          if (finalCategory !== tempItem.category) {
            console.log(`[Wardrobe] ✅ catégorie corrigée en "${finalCategory}" avant sauvegarde`);
            tempItem.category = finalCategory;
            setItems(prev => prev.map(it =>
              it.id === tempItem.id ? { ...it, category: finalCategory } : it
            ));
          }
        }

        // Fallback : si toujours pas de nom (IA n'a rien suggéré + user non fourni),
        // utiliser la catégorie comme nom par défaut (jamais d'empty name en DB).
        if (!tempItem.name || !tempItem.name.trim()) {
          tempItem.name = tempItem.category || 'Article';
          setItems(prev => prev.map(it =>
            it.id === tempItem.id ? { ...it, name: tempItem.name } : it
          ));
        }

        // 5. Sauvegarde en DB (avec le nom final + URL S3 publique)
        const itemToSave = {
          ...tempItem,
          image:     finalImageUrl,
          image_url: finalImageUrl,
        };
        // Inclure la metadata IA si disponible (évite re-analyse au reload)
        // v1.12.89 : injecter le pseudo (alias user) dans ai_metadata.pseudo
        // pour qu'il survive en DB sans nécessiter de migration de schéma.
        if (metadata && !metadata._rejected && !metadata._quotaExceeded) {
          if (tempItem.pseudo) metadata.pseudo = tempItem.pseudo;
          itemToSave.ai_metadata = metadata;
        }
        const savedItem = await addWardrobeItem(user.id, itemToSave, token);
        console.log(`[Wardrobe] 💾 sauvegardé en DB : ${tempItem.name} (DB id=${savedItem?.id})`);
        // v1.12.119 — popup passe en phase "done" (auto-close après 1.5s)
        setAddProgress('done');

        // v1.12.239 — Force le rafraîchissement du compteur IA dans le Profil après
        // un ajout réussi (l'event 'ai_usage_updated' n'arrive pas toujours si la
        // Lambda /ai-usage/check-and-increment a court-circuité ou si le cache a
        // été utilisé sans nouvel appel IA). On incrémente localement et on émet
        // l'event manuellement pour que ProfileScreen reflète le nouveau total.
        if (metadata && !metadata._rejected && !metadata._quotaExceeded) {
          try {
            const { incrementAIUsage, getAIUsageToday } = require('../services/PremiumService');
            const fresh = await getAIUsageToday(user?.id || user?.sub, user);
            // Si backend renvoie déjà un count à jour, on l'utilise. Sinon on incrémente local.
            const next = fresh > 0 ? fresh : await incrementAIUsage(user?.id || user?.sub);
            DeviceEventEmitter.emit('ai_usage_updated', {
              current: next,
              limit: userLimits?.ai_requests || FREE_LIMITS.ai_requests,
              tier: userLimits ? null : 'free',
            });
          } catch (e) {
            console.warn('[Wardrobe] AI counter sync failed:', e?.message);
          }
        }

        // 6. Remplacer le tempItem par l'item réel de la DB (vraie ID)
        // ═══ FIX 2026-04 : utiliser le retour DB pour éviter l'ID tempItem orphelin ═══
        // Avant, on gardait l'id local 'w123456' → à la prochaine recharge
        // l'item venait en double (un avec id local, un avec id DB).
        // Maintenant on remplace directement avec l'item renvoyé par l'API.
        const dbItem = savedItem && savedItem.id ? savedItem : null;
        const mergedItem = dbItem
          ? {
              ...dbItem,
              // Garde les valeurs locales qu'on a déjà pour affichage immédiat
              image:       finalImageUrl,
              image_url:   finalImageUrl,
              ai_metadata: metadata && !metadata._rejected && !metadata._quotaExceeded ? metadata : dbItem.ai_metadata,
            }
          : null;

        if (metadata && !metadata._rejected && !metadata._quotaExceeded) {
          // ═══ v1.12.12 (Fix refresh dressing) : UPSERT robuste ═══
          // Avant : setItems(prev => prev.map(...)) ne faisait que REMPLACER
          // l'item s'il existait dans prev. Si pour une raison quelconque le
          // tempItem avait disparu (focus reload, throttle, navigation...),
          // l'item DB n'était JAMAIS ajouté → user devait pull-to-refresh.
          // Maintenant : on remplace si présent, sinon on AJOUTE en queue.
          const itemToUpsert = mergedItem || { ...tempItem, ai_metadata: metadata, image: finalImageUrl, image_url: finalImageUrl };
          setItems(prev => {
            const idxTemp = prev.findIndex(it => it.id === tempItem.id);
            const idxReal = mergedItem?.id ? prev.findIndex(it => it.id === mergedItem.id) : -1;
            if (idxTemp >= 0) {
              // Remplacer le tempItem par la version DB
              const next = [...prev];
              next[idxTemp] = itemToUpsert;
              return next;
            }
            if (idxReal >= 0) {
              // Déjà l'item DB présent (cas reload entre-temps) : on le met à jour avec le metadata
              const next = [...prev];
              next[idxReal] = itemToUpsert;
              return next;
            }
            // Ni tempItem, ni item DB → AJOUTER en TÊTE (les + récents en haut)
            return [itemToUpsert, ...prev];
          });

          // ═══ v1.12.12 : Invalider le cache local pour forcer un refresh ═══
          // Sinon au prochain cold start ou changement d'écran, l'AsyncStorage
          // pourrait contenir l'ancien dressing sans ce nouvel item.
          try {
            const CACHE_KEY = `outfit_wardrobe_cache_${user.id}`;
            await AsyncStorage.removeItem(CACHE_KEY);
            console.log('[Wardrobe] 💾 cache invalidé après ajout');
          } catch {}

          // ═══ v1.12.12 : Émettre un event pour les autres écrans ═══
          // Permet à LooksScreen, HomeScreen, etc. de savoir que le dressing
          // a changé et de re-générer si nécessaire.
          try {
            const { DeviceEventEmitter } = require('react-native');
            // v1.13.89 — _internalSkipReload : l'item est DÉJÀ upserté localement
            //   ci-dessus. Sans ce flag, le listener de WardrobeScreen relançait un
            //   loadWardrobe(true) (reload DB complet forcé) → re-render de toute la
            //   liste + RE-EXÉCUTION du batch d'analyse IA (5 items séquentiels +
            //   JSON.stringify du dressing à chaque item) → l'app ralentissait /
            //   freezait après chaque ajout. Les AUTRES écrans (Looks/Home) ne
            //   lisent pas ce flag → ils restent notifiés du changement.
            DeviceEventEmitter.emit('wardrobe_updated', { action: 'add', item: itemToUpsert, _internalSkipReload: true });
          } catch {}

          // Feedback visuel pour l'utilisateur
          const color   = metadata.primaryColor || 'couleur indéterminée';
          const pattern = metadata.pattern && metadata.pattern !== 'uni' ? ` ${metadata.pattern}` : '';
          const material = metadata.material ? ` · ${metadata.material}` : '';
          const detected = `${color}${pattern}${material}`;
          Alert.alert(
            t('item_analyzed_title'),
            t('item_analyzed_msg').replace('{name}', tempItem.name).replace('{detected}', detected),
            [{ text: t('perfect_excl'), style: 'default' }],
            { cancelable: true }
          );
        } else {
          // Pas d'analyse IA (quota, erreur réseau) → juste s'assurer que l'image S3 est à jour
          // ═══ v1.12.12 : même UPSERT que ci-dessus ═══
          const itemToUpsert = mergedItem || { ...tempItem, image: finalImageUrl, image_url: finalImageUrl };
          setItems(prev => {
            const idxTemp = prev.findIndex(it => it.id === tempItem.id);
            const idxReal = mergedItem?.id ? prev.findIndex(it => it.id === mergedItem.id) : -1;
            if (idxTemp >= 0) {
              const next = [...prev];
              next[idxTemp] = itemToUpsert;
              return next;
            }
            if (idxReal >= 0) {
              const next = [...prev];
              next[idxReal] = itemToUpsert;
              return next;
            }
            return [...prev, itemToUpsert];
          });

          // Invalidation cache + event (même comportement)
          try {
            const CACHE_KEY = `outfit_wardrobe_cache_${user.id}`;
            await AsyncStorage.removeItem(CACHE_KEY);
          } catch {}
          try {
            const { DeviceEventEmitter } = require('react-native');
            // v1.13.89 — cf. ci-dessus : item déjà upserté localement → skip le
            //   reload DB forcé de WardrobeScreen (évite freeze post-ajout).
            DeviceEventEmitter.emit('wardrobe_updated', { action: 'add', item: itemToUpsert, _internalSkipReload: true });
          } catch {}
        }
      } catch(e) {
        console.warn('addWardrobeItem:', e.message);
        // v1.12.119 — popup passe en phase "error"
        setAddProgressError(e.message || "L'article n'a pas pu être ajouté.");
        setAddProgress('error');
      }
    }
  };

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      {/* Header */}
      <View style={[s.header, { backgroundColor: theme.bg, borderBottomColor: theme.bgBorder }]}>
        <View style={{ flex: 1 }}>
          <Text style={[s.title, { color: theme.text }]}>{t('my_wardrobe')}</Text>
          {/* v1.10.85 : ligne "X pièces · X tenues · X suggestions" supprimée
              (les chiffres sont déjà visibles dans les badges des onglets ci-dessous) */}
        </View>
        <Tooltip text={t('tooltip_create_new_look')}>
          <TouchableOpacity
            style={s.createLookBtn}
            // v1.12.279 — toujours actif : l'user peut composer depuis le catalogue
            // même si son dressing est vide (CreateLookModal a un onglet Catalogue).
            onPress={() => setShowCreateLook(true)}
            activeOpacity={0.85}
          >
            <Text style={s.createLookIcon}>✨</Text>
            <Text style={s.createLookText}>{t('create')}</Text>
          </TouchableOpacity>
        </Tooltip>
        <Tooltip text={t('tooltip_add_item_photo_or_url')}>
          <TouchableOpacity style={s.addBtn} onPress={async () => {
              // v1.13.62 — PERF : un seul Promise.all (2 fetchs parallèles) au lieu
              // de 4 appels (le combiné + 2 guardCheck séquentiels redondants) qui
              // rendaient le bouton "+" lent à s'ouvrir. On réutilise les résultats.
              try {
                const { checkLimit } = require('../services/PremiumService');
                const [wardrobeRes, aiRes] = await Promise.all([
                  checkLimit('wardrobe', (items||[]).length, user),
                  checkLimit('ai', 0, user),
                ]);
                if (!wardrobeRes.allowed && !aiRes.allowed) {
                  setLimitInfo({ trigger: 'wardrobe_and_ai', tier: wardrobeRes.tier || 'free', limit: wardrobeRes.limit, current: wardrobeRes.current, reason: 'wardrobe_and_ai_both_reached' });
                  setPremiumTrigger('wardrobe_and_ai'); setShowPremium(true); return;
                }
                if (!wardrobeRes.allowed) {
                  setLimitInfo({ trigger: 'wardrobe', tier: wardrobeRes.tier || 'free', limit: wardrobeRes.limit, current: wardrobeRes.current, reason: wardrobeRes.reason });
                  setPremiumTrigger('wardrobe'); setShowPremium(true); return;
                }
                if (!aiRes.allowed) {
                  setLimitInfo({ trigger: 'ai', tier: aiRes.tier || 'free', limit: aiRes.limit, current: aiRes.current, reason: aiRes.reason });
                  setPremiumTrigger('ai'); setShowPremium(true); return;
                }
              } catch (e) {
                // fail-open : si le check échoue (réseau), on ouvre quand même —
                // le quota est de toute façon ré-appliqué à l'étape d'analyse.
                console.warn('[WardrobeAddBtn] check failed → open anyway:', e?.message);
              }
              handleOpenAdd();
            }}>
            <Text style={s.addBtnText}>＋</Text>
          </TouchableOpacity>
        </Tooltip>
      </View>
      <View style={s.divider} />

      {/* Tabs */}
      <View style={s.tabs}>
        {[
          { key: 'items',       label: t('my_pieces'),  count: items.length },
          { key: 'mylooks',     label: t('my_looks'),   count: userCreatedLooks.length },
        ].map(tb => (
          <TouchableOpacity key={tb.key}
            style={[s.tab, tab === tb.key && s.tabActive]}
            onPress={() => {
              setTab(tb.key);
              // ═══ Scroll en haut au changement d'onglet ═══
              try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
            }}>
            <Text style={[s.tabLabel, tab === tb.key && s.tabLabelActive]}>{tb.label}</Text>
            {tb.count > 0 && (
              <View style={[s.tabBadge, tab === tb.key && s.tabBadgeActive]}>
                <Text style={[s.tabBadgeText, tab === tb.key && s.tabBadgeTextActive]}>{tb.count}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>

      {tab === 'items' && (
        <>
          {/* v1.12.608 — V5.6 : bandeau items cachés (au-delà du quota tier) */}
          {(() => {
            const hiddenCount = (items || []).filter(i => i._hidden).length;
            if (hiddenCount === 0) return null;
            const isFR = !lang || (typeof lang === 'string' && lang.startsWith('fr'));
            return (
              <TouchableOpacity
                style={s.hiddenBanner}
                onPress={() => { try { navigation.navigate('Shop'); } catch {} }}
                activeOpacity={0.85}
              >
                <Text style={s.hiddenBannerIcon}>🔒</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.hiddenBannerTitle}>
                    {isFR
                      ? `${hiddenCount} article${hiddenCount > 1 ? 's' : ''} masqué${hiddenCount > 1 ? 's' : ''}`
                      : `${hiddenCount} item${hiddenCount > 1 ? 's' : ''} hidden`}
                  </Text>
                  <Text style={s.hiddenBannerSub}>
                    {isFR
                      ? 'Au-delà du quota de votre palier. Upgradez ou achetez un Boost +50 pour les débloquer.'
                      : 'Beyond your tier quota. Upgrade or buy a +50 Boost to unlock.'}
                  </Text>
                </View>
                <Text style={s.hiddenBannerArrow}>›</Text>
              </TouchableOpacity>
            );
          })()}
          {/* v1.12.91 — Bandeau stats (Pièces/Catégories/Analysés IA) supprimé */}
          {/* sur demande user. La carte de progression IA reste affichée tant que */}
          {/* tous les articles ne sont pas analysés. */}
          {items.length > 0 && (() => {
            const analyzedCount = items.filter(i => i.ai_metadata?.analyzedAt).length;
            const totalCount    = items.length;
            const pendingCount  = totalCount - analyzedCount;
            const pct           = Math.round((analyzedCount / Math.max(1, totalCount)) * 100);
            const allAnalyzed   = pendingCount === 0;

            return (
              <>
                {/* ═══ Carte de progression IA : visible uniquement pendant l'analyse ═══ */}
                {/* v1.12.515 — Pill "Dressing entièrement analysé" supprimée à la demande user */}
                {allAnalyzed ? null : (
                  // ═══ VERSION COMPLÈTE : affichée seulement pendant l'analyse ═══
                  <View style={s.aiProgressCard}>
                    <View style={s.aiProgressHeader}>
                      <View style={{ flex: 1 }}>
                        <View style={s.aiProgressTitleRow}>
                          <Text style={s.aiProgressIcon}>🤖</Text>
                          <Text style={s.aiProgressTitle}>{t('ai_analysis_in_progress')}</Text>
                        </View>
                        <Text style={s.aiProgressSub}>
                          {pendingCount === 1
                            ? t('one_item_pending_analysis')
                            : t('items_pending_analysis').replace('{count}', String(pendingCount))
                          }
                        </Text>
                      </View>
                      <View style={s.aiProgressPctBadge}>
                        <Text style={s.aiProgressPctText}>{pct}%</Text>
                      </View>
                    </View>

                    {/* Barre de progression */}
                    <View style={s.aiProgressBarWrap}>
                      <View style={s.aiProgressBarBg}>
                        <View
                          style={[
                            s.aiProgressBarFill,
                            { width: `${pct}%`, backgroundColor: '#D4AF37' },
                          ]}
                        />
                      </View>
                    </View>

                    <View style={s.aiProgressFooter}>
                      <View style={s.aiProgressFooterItem}>
                        <View style={[s.aiProgressDot, { backgroundColor: '#4CAF50' }]} />
                        <Text style={s.aiProgressFooterText}>
                          <Text style={{ fontWeight: '700' }}>{analyzedCount}</Text> {t('analyzed_label')}
                        </Text>
                      </View>
                      <View style={s.aiProgressFooterItem}>
                        <View style={[s.aiProgressDot, { backgroundColor: '#E0E0E0' }]} />
                        <Text style={s.aiProgressFooterText}>
                          <Text style={{ fontWeight: '700' }}>{pendingCount}</Text> {t('remaining_label')}
                        </Text>
                      </View>
                      <Text style={s.aiProgressFooterInfo}>{t('auto_analysis_background')}</Text>
                    </View>
                  </View>
                )}
              </>
            );
          })()}

          {/* Search */}
          <View style={s.searchRow}>
            <View style={s.searchBox}>
              <Text style={s.searchIcon}>🔍</Text>
              <TextInput style={s.searchInput} placeholder={t('search') + '...'}
                placeholderTextColor={colors.text.ghost}
                value={search} onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
                contextMenuHidden={false}
                importantForAutofill="no"
                returnKeyType="search" />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Text style={s.clearBtn}>✕</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Categories — avec compteur par catégorie.
              v1.12.117 : ChipsRow s'auto-adapte (scroll horizontal sur native,
              flex-wrap sur web pour tout voir sans scroller). */}
          <View style={s.catScrollWrap}>
            <ChipsRow contentContainerStyle={s.catRow}>
              {getWardrobeCats(user?.gender).map(cat => {
                const count = cat === 'Tout' ? items.length : items.filter(i => i.category === cat).length;
                const CAT_KEYS = { 'Tout':'all_categories', 'Robes':'cat_dresses', 'Hauts':'cat_tops', 'Vestes':'cat_jackets', 'Manteaux':'cat_coats', 'Bas':'cat_bottoms', 'Maillots':'cat_swimwear', 'Chaussures':'cat_shoes', 'Sacs':'cat_bags', 'Bijoux':'cat_jewelry', 'Accessoires':'cat_accessories' };
                const catLabel = CAT_KEYS[cat] ? t(CAT_KEYS[cat]) : cat;
                return (
                  <TouchableOpacity key={cat} style={[s.catChipPremium, category === cat && s.catChipPremiumActive]}
                    onPress={() => {
                      setCategory(cat);
                      try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
                    }}>
                    <Text style={[s.catLabelPremium, category === cat && s.catLabelPremiumActive]}>{catLabel}</Text>
                    {count > 0 && (
                      <View style={[s.catCountBubble, category === cat && s.catCountBubbleActive]}>
                        <Text style={[s.catCountText, category === cat && s.catCountTextActive]}>{count}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </ChipsRow>
          </View>

          <ScrollView
            ref={scrollRef}
            contentContainerStyle={{ paddingBottom: 32 }}
            refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
            removeClippedSubviews={Platform.OS === 'android'}
            scrollEventThrottle={16}
          >
            <View style={s.grid}>
              {filtered.map(item => (
                <TouchableOpacity key={`grid_${item.id || item.name || 'x'}_${item.created_at || ''}`} style={[s.gridCard, shadow.sm,
                  selected === item.id && s.gridCardSelected,
                  item._hidden && { opacity: 0.55 }]}
                  onPress={() => {
                    // v1.12.580 — V5.1-bis : items hidden = paywall upsell wardrobe
                    if (item._hidden) {
                      setLimitInfo({ trigger: 'wardrobe', limit: 0, current: 1, nextResetDate: null });
                      setShowPremium(true);
                      return;
                    }
                    setSelected(selected === item.id ? null : item.id);
                  }}
                  activeOpacity={0.8}>
                  <View style={s.gridImg}>
                    {item.image
                      ? <WardrobeItemImage uri={item.image} emoji={item.emoji} hidden={item._hidden} />
                      : <Text style={[s.gridEmoji, item._hidden && { opacity: 0.4 }]}>{item.emoji}</Text>
                    }
                    {/* Badge IA : article analysé par Claude Vision */}
                    {item.ai_metadata?.analyzedAt && !item._hidden && (
                      <View style={s.aiAnalyzedBadge}>
                        <Text style={s.aiAnalyzedText}>✨ IA</Text>
                      </View>
                    )}
                    {/* v1.12.580 — Cadenas si item hidden (au-delà du quota tier + bonus) */}
                    {item._hidden && (
                      <View style={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: [{ translateX: -22 }, { translateY: -22 }],
                        width: 44, height: 44, borderRadius: 22,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Text style={{ fontSize: 22 }}>🔒</Text>
                      </View>
                    )}
                  </View>
                  <View style={s.gridInfo}>
                    <Text style={s.gridName} numberOfLines={1}>{displayName(item, lang)}</Text>
                    {/* v1.12.525/529 — Couleur principale IA en couleur, sous le nom (traduite EN) */}
                    {(() => {
                      const aiColor = item.ai_metadata?.primaryColor;
                      const rawLabel = aiColor || item.color;
                      if (!rawLabel) return null;
                      const en = lang === 'en';
                      // Traduit en EN si la langue le requiert, sinon capitalise FR
                      const translated = en ? translateAIColor(rawLabel, true) : rawLabel;
                      const colorLabel = (translated || '').charAt(0).toUpperCase() + (translated || '').slice(1);
                      // getColorHex utilise la version FR/originale pour matcher la couleur réelle
                      const hex = getColorHex(rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1));
                      const isPale = ['#F8F8F8','#FFFFF0','#FFF8E7','#EDE8D0'].includes(hex);
                      return (
                        <View style={s.gridColorRow}>
                          <View style={[s.colorDot, { backgroundColor: hex, borderColor: isPale ? '#CCC' : 'transparent', borderWidth: isPale ? 1 : 0 }]} />
                          <Text style={[s.gridColorBold, { color: isPale ? '#666' : hex }]} numberOfLines={1}>{colorLabel}</Text>
                        </View>
                      );
                    })()}
                    {!!item.brand && <Text style={s.gridBrand}>{item.brand}</Text>}
                  </View>
                  {selected === item.id && (
                    <View style={s.selectedBadge}><Text style={s.selectedBadgeText}>✓</Text></View>
                  )}
                </TouchableOpacity>
              ))}

              {/* Add card */}
              <TouchableOpacity style={[s.addCard, shadow.sm]} onPress={async () => {
                const okWardrobe = await guardCheck('wardrobe', (items||[]).length, false);
                if (!okWardrobe) return;
                const okAi = await guardCheck('ai', 0, false);
                if (!okAi) return;
                handleOpenAdd();
              }}>
                <Text style={s.addCardPlus}>＋</Text>
                <Text style={s.addCardLabel}>{t('add')}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </>
      )}

      {tab === 'mylooks' && (
        /* ══════════════════════════════════════════════════════
           MES LOOKS — Looks créés manuellement par l'utilisateur
           ══════════════════════════════════════════════════════ */
        <ScrollView
          contentContainerStyle={{ padding: spacing.xl, paddingBottom: 60 }}
          refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {/* ── Header ── */}
          <View style={s.comboHub}>
            <View style={s.comboHubLeft}>
              <Text style={s.comboHubBadge}>{'✨ ' + t('my_creations_caps')}</Text>
              <Text style={s.comboHubTitle}>{t('my_looks')}</Text>
              <Text style={s.comboHubSub}>
                {userCreatedLooks.length > 0
                  ? (userCreatedLooks.length === 1
                      ? t('one_personal_look')
                      : t('personal_looks_count').replace('{count}', String(userCreatedLooks.length)))
                  : t('create_own_looks_hint')}
              </Text>
            </View>
          </View>

          {/* Bouton créer un look (dans l'onglet Mes looks) */}
          <Tooltip text={t('tooltip_create_complete_look')}>
            <TouchableOpacity
              style={s.newLookBigBtn}
              onPress={() => setShowCreateLook(true)}
              activeOpacity={0.85}
            >
              <Text style={s.newLookBigBtnIcon}>＋</Text>
              <Text style={s.newLookBigBtnText}>{t('create_new_look')}</Text>
            </TouchableOpacity>
          </Tooltip>

          {/* v1.12.319 — Recherche dans Mes Looks : nom du look, article (nom/pseudo),
               catégorie, couleur, ou article du dressing */}
          {userCreatedLooks.length > 0 && (
            <View style={{
              flexDirection: 'row', alignItems: 'center',
              backgroundColor: '#FFFFFF', borderRadius: 12,
              borderWidth: 1, borderColor: '#EAE6E0',
              paddingHorizontal: 14, paddingVertical: 10,
              marginBottom: 12, gap: 8,
            }}>
              <Text style={{ fontSize: 14, color: '#A89E94' }}>🔍</Text>
              <TextInput
                value={looksSearchQ}
                onChangeText={setLooksSearchQ}
                placeholder={t('search_in_my_looks_placeholder')}
                placeholderTextColor="#A89E94"
                style={{ flex: 1, fontSize: 14, color: '#1A1A1A', paddingVertical: 0,
                  ...(typeof window !== 'undefined' && window.document ? { outlineWidth: 0 } : {}) }}
                returnKeyType="search"
              />
              {looksSearchQ.length > 0 && (
                <TouchableOpacity onPress={() => setLooksSearchQ('')} hitSlop={{top:8,right:8,bottom:8,left:8}}>
                  <Text style={{ fontSize: 14, color: '#A89E94', paddingHorizontal: 4 }}>✕</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* État vide */}
          {userCreatedLooks.length === 0 && (
            <View style={s.comboEmptyState}>
              <Text style={s.comboEmptyIcon}>✨</Text>
              <Text style={s.comboEmptyTitle}>{t('no_look_created')}</Text>
              <Text style={s.comboEmptyDesc}>
                {t('create_first_look_desc')}
              </Text>
            </View>
          )}

          {/* v1.12.319 — applique le filtre de recherche aux looks affichés */}
          {(() => {
            const q = (looksSearchQ || '').toLowerCase().trim();
            const matches = q.length === 0 ? userCreatedLooks : userCreatedLooks.filter(look => {
              const hay = [
                look.name,
                look.occasion,
                ...(look.items || []).map(it => it.name),
                ...(look.items || []).map(it => it.pseudo),
                ...(look.items || []).map(it => it.ai_metadata?.pseudo),
                ...(look.items || []).map(it => it.category),
                ...(look.items || []).map(it => it.brand),
                ...(look.items || []).map(it => it.color),
                ...(look.items || []).flatMap(it => it.ai_metadata?.colors || []),
                ...(look.items || []).map(it => it.ai_metadata?.dominantColor),
              ].filter(Boolean).join(' ').toLowerCase();
              return hay.includes(q);
            });
            if (q.length > 0 && matches.length === 0) {
              return (
                <View style={s.comboEmptyState}>
                  <Text style={s.comboEmptyIcon}>🔍</Text>
                  <Text style={s.comboEmptyTitle}>{t('no_results')}</Text>
                  <Text style={s.comboEmptyDesc}>{t('change_search_or_category')}</Text>
                </View>
              );
            }
            return matches.map((look, idx) => {
            const validSuggs = (look.suggestions || []).filter(p =>
              p && p.id &&
              (p.image || p.image_url) &&
              typeof (p.image || p.image_url) === 'string' &&
              /^https?:\/\//.test(p.image || p.image_url) &&
              (p.link || p.affiliateUrl || p.url) &&
              (p.price || 0) > 0
            );
            const isLoadingRefresh = loadingRefreshLookId === look.id;
            return (
              <View key={look.id} style={[s.userLookCard, shadow.md]}>
                <View style={s.userLookHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.userLookOccasion}>{look.occasion?.toUpperCase()}</Text>
                    <Text style={s.userLookName}>{look.name}</Text>
                  </View>
                  {/* v1.12.333 — Bouton supprimer (remplace try-on dans la barre, à côté du calendrier) */}
                  <Tooltip text={t('delete_this_look')}>
                    <TouchableOpacity
                      style={[s.userLookRefreshBtn, { marginRight: 6 }]}
                      onPress={async () => {
                        // v1.12.286 — showAppAlert (web-compat) + LookService.deleteLook
                        const idx = await showAppAlert(
                          t('delete_this_look_q'),
                          t('action_irreversible'),
                          [
                            { text: t('cancel'), style: 'cancel' },
                            { text: t('delete'), style: 'destructive' },
                          ]
                        );
                        if (idx !== 1) return;
                        setUserCreatedLooks(prev => prev.filter(l => l.id !== look.id));
                        try {
                          const { deleteLook } = require('../services/LookService');
                          await deleteLook(look.id, { user, getValidToken });
                        } catch(e) { console.warn('[Wardrobe] deleteLook failed:', e?.message); }
                      }}
                      activeOpacity={0.8}>
                      <Text style={{ fontSize: 16 }}>🗑</Text>
                    </TouchableOpacity>
                  </Tooltip>
                  {/* v1.12.250 — Bouton calendrier (raccourci ajout) */}
                  <Tooltip text={t('add_outfit_to_calendar_tooltip')}>
                    <TouchableOpacity
                      style={[s.userLookRefreshBtn, { marginRight: 6 }]}
                      onPress={() => setCalendarShortcut({
                        look: {
                          id: look.id,
                          name: look.name || t('my_look_default'),
                          items: (look.items || []).map(it => ({
                            id: it.id, name: it.name, category: it.category,
                            image_url: it.image_url || it.image, brand: it.brand,
                          })),
                          occasion: look.occasion || null,
                        },
                        source: 'mes_looks',
                      })}
                      activeOpacity={0.8}>
                      <CalendarIcon size={16} color="#B8860B" dot="#D4AF72" />
                    </TouchableOpacity>
                  </Tooltip>
                  <Tooltip text={t('tooltip_regenerate_shopping_suggestions')}>
                    <TouchableOpacity
                      style={s.userLookRefreshBtn}
                      onPress={() => refreshLookSuggestions(look)}
                      disabled={isLoadingRefresh}
                      activeOpacity={0.8}
                    >
                      {isLoadingRefresh
                        ? <ActivityIndicator size="small" color="#B8860B" />
                        : <Text style={{ fontSize: 16, color: '#B8860B' }}>↻</Text>
                      }
                    </TouchableOpacity>
                  </Tooltip>
                </View>
                {/* v1.12.832 — Boutons Try-on + Flat-lay (meme rendu que Tenue du jour / Selection styliste) */}
                <View style={{
                  flexDirection: 'row', gap: 8,
                  paddingHorizontal: 12, paddingTop: 4, paddingBottom: 8,
                }}>
                  <TouchableOpacity
                    style={{
                      flex: 1,
                      backgroundColor: '#1A1A1A',
                      paddingVertical: 12,
                      borderRadius: 8,
                      alignItems: 'center', justifyContent: 'center',
                      borderWidth: 1, borderColor: '#D4AF72',
                      flexDirection: 'row', gap: 6,
                    }}
                    onPress={() => openTryOnVertex(look)}
                    activeOpacity={0.85}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                      ✨ {t('try_on_caps')}
                    </Text>
                  </TouchableOpacity>
                  <FlatLayButton outfit={look} style={{ borderRadius: 8 }} />
                </View>
                {/* Articles du look (dressing + catalogue) — v1.12.517 cliquables → ProductDetailModal */}
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, padding: 12 }}>
                  {(look.items || []).map(it => (
                    <TouchableOpacity
                      key={it.id}
                      style={s.userLookItem}
                      onPress={() => setSelectedProduct(it)}
                      activeOpacity={0.85}
                    >
                      {(it.image_url || it.image) ? (
                        <Image source={{ uri: it.image_url || it.image }} style={s.userLookImg} />
                      ) : (
                        <View style={[s.userLookImg, { backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' }]}>
                          <Text style={{ fontSize: 20, opacity: 0.3 }}>👗</Text>
                        </View>
                      )}
                      <Text style={s.userLookItemLabel} numberOfLines={1}>{it.brand || it.name}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                {/* ═══ Suggestions partenaires (design unifié : badge promo + prix barré + voir plus) ═══ */}
                {validSuggs.length > 0 ? (() => {
                  const isExpanded = !!expandedLookIds[look.id];
                  const SUGGS_COMPACT = 8;
                  const displayItems = isExpanded ? validSuggs : validSuggs.slice(0, SUGGS_COMPACT);
                  const canShowMore = validSuggs.length > SUGGS_COMPACT;
                  return (
                  <View style={s.userLookSuggsBlock}>
                    {/* Header avec bouton Voir plus/Réduire */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 12, marginBottom: 6 }}>
                      <Text style={s.userLookSuggsTitle}>{'🛍️ ' + t('complete_or_replace') + ' (' + validSuggs.length + ')'}</Text>
                      {(canShowMore || isExpanded) && (
                        <TouchableOpacity
                          onPress={() => setExpandedLookIds(prev => ({ ...prev, [look.id]: !isExpanded }))}
                          activeOpacity={0.7}
                          style={{
                            paddingHorizontal: 10, paddingVertical: 4,
                            borderRadius: 12,
                            backgroundColor: isExpanded ? colors.text.primary : '#F9F7F3',
                            borderWidth: 1,
                            borderColor: isExpanded ? colors.text.primary : '#EDEBE8',
                          }}
                        >
                          <Text style={{ fontSize: 10, fontWeight: '600', letterSpacing: 0.3, color: isExpanded ? '#fff' : colors.text.primary }}>
                            {isExpanded ? '↑ ' + t('collapse') : '+ ' + t('see').replace('{count}', String(validSuggs.length - SUGGS_COMPACT))}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>

                    {/* Compact : scroll horizontal */}
                    {!isExpanded ? (
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingHorizontal: 12, paddingBottom: 8 }}>
                        {displayItems.map((p, i) => {
                          const hasPromo = p.originalPrice && p.originalPrice > p.price + 0.5;
                          const discountPct = hasPromo ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100) : 0;
                          return (
                          <TouchableOpacity
                            key={p.id || i}
                            style={s.userLookSuggCard}
                            onPress={() => setSelectedProduct(p)}
                            activeOpacity={0.85}
                          >
                            <View style={{ position: 'relative' }}>
                              <Image source={{ uri: p.image || p.image_url }} style={s.userLookSuggImg} />
                              {hasPromo && (
                                <View style={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#FF3B30', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 4 }}>
                                  <Text style={{ fontSize: 8, fontWeight: '800', color: '#fff', letterSpacing: 0.3 }}>-{discountPct}%</Text>
                                </View>
                              )}
                              {p._isReplacement && (
                                <View style={{ position: 'absolute', top: 4, left: 4, backgroundColor: '#1A1A1A', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 4 }}>
                                  <Text style={{ fontSize: 8, fontWeight: '800', color: '#D4AF72', letterSpacing: 0.3 }}>↺ MIEUX</Text>
                                </View>
                              )}
                            </View>
                            {p._isReplacement && p._replacesItem && (
                              <Text style={{ fontSize: 9, color: '#888', fontStyle: 'italic' }} numberOfLines={1}>
                                Remplace {(p._replacesItem.category || '').toLowerCase()}
                              </Text>
                            )}
                            <Text style={s.userLookSuggBrand} numberOfLines={1}>{p.brand || p.partner}</Text>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 4 }}>
                              <Text style={[s.userLookSuggPrice, hasPromo && { color: '#FF3B30' }]}>{(Number(p.price) || 0).toFixed(0)}€</Text>
                              {hasPromo && (
                                <Text style={{ fontSize: 9, color: '#999', textDecorationLine: 'line-through' }}>
                                  {(Number(p.originalPrice) || 0).toFixed(0)}€
                                </Text>
                              )}
                            </View>
                          </TouchableOpacity>
                          );
                        })}
                        <TouchableOpacity
                          style={s.userLookLoadMoreCard}
                          onPress={() => refreshLookSuggestions(look, true)}
                          disabled={isLoadingRefresh}
                          activeOpacity={0.85}
                        >
                          {isLoadingRefresh ? (
                            <ActivityIndicator size="small" color="#B8860B" />
                          ) : (
                            <>
                              <View style={s.userLookLoadMoreIcon}>
                                <Text style={{ fontSize: 18 }}>✨</Text>
                              </View>
                              <Text style={s.userLookLoadMoreText}>{t('more_items_short')}</Text>
                            </>
                          )}
                        </TouchableOpacity>
                      </ScrollView>
                    ) : (
                      /* Expand : grille 2 colonnes */
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingHorizontal: 12, paddingBottom: 10 }}>
                        {displayItems.map((p, i) => {
                          const hasPromo = p.originalPrice && p.originalPrice > p.price + 0.5;
                          const discountPct = hasPromo ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100) : 0;
                          return (
                          <TouchableOpacity
                            key={`exp_${p.id || i}`}
                            style={{
                              width: '48%',
                              backgroundColor: '#fff',
                              borderRadius: 10,
                              overflow: 'hidden',
                              borderWidth: 1,
                              borderColor: '#EDEBE8',
                            }}
                            onPress={() => setSelectedProduct(p)}
                            activeOpacity={0.85}
                          >
                            <View style={{ position: 'relative', width: '100%', aspectRatio: 0.85 }}>
                              <Image source={{ uri: p.image || p.image_url }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                              {hasPromo && (
                                <View style={{ position: 'absolute', top: 6, right: 6, backgroundColor: '#FF3B30', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 4 }}>
                                  <Text style={{ fontSize: 10, fontWeight: '800', color: '#fff', letterSpacing: 0.3 }}>-{discountPct}%</Text>
                                </View>
                              )}
                              {p._isReplacement && (
                                <View style={{ position: 'absolute', top: 6, left: 6, backgroundColor: '#1A1A1A', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 4 }}>
                                  <Text style={{ fontSize: 9, fontWeight: '800', color: '#D4AF72', letterSpacing: 0.3 }}>↺ MIEUX</Text>
                                </View>
                              )}
                            </View>
                            <View style={{ padding: 8 }}>
                              {p._isReplacement && p._replacesItem && (
                                <Text style={{ fontSize: 9, color: '#888', fontStyle: 'italic', marginBottom: 2 }} numberOfLines={1}>
                                  Remplace votre {(p._replacesItem.category || '').toLowerCase()}
                                </Text>
                              )}
                              <Text style={{ fontSize: 9, letterSpacing: 1.2, color: '#999', textTransform: 'uppercase' }} numberOfLines={1}>
                                {p.brand || p.partner || ''}
                              </Text>
                              <Text style={{ fontSize: 11, fontWeight: '500', color: '#1A1A1A', lineHeight: 15, marginVertical: 2 }} numberOfLines={2}>
                                {p.name || ''}
                              </Text>
                              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 5 }}>
                                <Text style={{ fontSize: 12, fontWeight: '700', color: hasPromo ? '#FF3B30' : '#1A1A1A' }}>
                                  {(Number(p.price) || 0).toFixed(0)} €
                                </Text>
                                {hasPromo && (
                                  <Text style={{ fontSize: 10, color: '#999', textDecorationLine: 'line-through' }}>
                                    {(Number(p.originalPrice) || 0).toFixed(0)} €
                                  </Text>
                                )}
                              </View>
                            </View>
                          </TouchableOpacity>
                          );
                        })}
                        {/* Bouton Plus d'articles en grille */}
                        <TouchableOpacity
                          style={{
                            width: '48%',
                            backgroundColor: '#FFF9E6',
                            borderRadius: 10,
                            borderWidth: 1,
                            borderColor: '#F5DEB3',
                            borderStyle: 'dashed',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 200,
                          }}
                          onPress={() => refreshLookSuggestions(look, true)}
                          disabled={isLoadingRefresh}
                          activeOpacity={0.85}
                        >
                          {isLoadingRefresh ? (
                            <ActivityIndicator size="small" color="#B8860B" />
                          ) : (
                            <>
                              <Text style={{ fontSize: 28, marginBottom: 6 }}>✨</Text>
                              <Text style={{ fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5, textAlign: 'center', paddingHorizontal: 10 }}>
                                {t('generate_more_items')}
                              </Text>
                            </>
                          )}
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                  );
                })() : (
                  <View style={{ padding: 16, alignItems: 'center' }}>
                    <Text style={{ fontSize: 11, color: '#999', marginBottom: 10 }}>{t('no_valid_suggestions_yet')}</Text>
                    <TouchableOpacity
                      onPress={() => refreshLookSuggestions(look)}
                      disabled={isLoadingRefresh}
                      style={{
                        flexDirection: 'row', gap: 8, alignItems: 'center',
                        backgroundColor: '#FFF9E6',
                        borderWidth: 1, borderColor: '#F5DEB3',
                        borderRadius: 100,
                        paddingHorizontal: 16, paddingVertical: 10,
                      }}
                      activeOpacity={0.85}
                    >
                      {isLoadingRefresh
                        ? <ActivityIndicator size="small" color="#B8860B" />
                        : <>
                            <Text style={{ fontSize: 14 }}>✨</Text>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#B8860B', letterSpacing: 0.5 }}>
                              {t('generate_suggestions')}
                            </Text>
                          </>
                      }
                    </TouchableOpacity>
                  </View>
                )}

                {/* v1.12.333 — Bouton Try-on (remplace le bouton supprimer en bas) */}
                <TouchableOpacity
                  style={s.userLookDeleteBtn}
                  onPress={() => openTryOnVertex(look)}
                  activeOpacity={0.85}
                >
                  <Text style={s.userLookDeleteBtnText}>{'✨  ' + (lang === 'en' ? 'Try-on' : 'Essai')}</Text>
                </TouchableOpacity>
              </View>
            );
          });
          })()}
        </ScrollView>
      )}


      {/* v1.12.194 + v1.12.200 — Guide photo (à chaque ajout, opt-out possible).
          Le gender est passé pour afficher les exemples adaptés F ou M. */}
      <WardrobePhotoGuideModal
        visible={showPhotoGuide}
        onContinue={handlePhotoGuideContinue}
        onClose={handlePhotoGuideClose}
        gender={user?.gender === 'M' ? 'M' : 'F'}
      />

      {/* Modal ajouter une pièce */}
      <AddItemModal
        visible={showAdd}
        onClose={() => setShowAdd(false)}
        onAdd={onAddItem}
        user={user}
        onPickUrl={() => setShowAddUrl(true)}
        onAILimitReached={(current, limit) => {
          // Déclenche le popup upgrade pour IA
          setShowAdd(false);
          setTimeout(() => guardCheck('ai', current, true), 300);
        }}
      />

      {/* v1.12.119 — Popup de progression de l'ajout (upload + analyse IA) */}
      <WardrobeAddProgressModal
        state={addProgress}
        errorMessage={addProgressError}
        onClose={() => { setAddProgress(null); setAddProgressError(null); }}
      />

      {/* Modal ajouter par URL iQueens */}
      <AddByUrlModal
        visible={showAddUrl}
        onClose={() => setShowAddUrl(false)}
        onAdd={(newItem) => {
          setShowAddUrl(false);
          // v1.12.73 — Si l'item est déjà inséré côté backend (flow "Coller URL
          // image" via /wardrobe/from-url), pas besoin de re-déclencher /analyze-item.
          // On reload juste le dressing.
          if (newItem?._alreadyAdded) {
            console.log(`[Wardrobe] item déjà inséré côté backend (id=${newItem._itemId}), reload`);
            // v1.12.119 — feedback visuel ajout réussi via popup
            setAddProgress('done');
            try { loadWardrobe?.(true); } catch {}
            return;
          }
          onAddItem(newItem);
        }}
        // v1.10.87 : si l'extraction URL échoue, l'utilisateur peut basculer
        // vers l'ajout par photo via un Alert popup. On ferme le modal URL
        // et on ouvre l'AddItemModal qui contient le choix photo/galerie.
        onSwitchToPhoto={() => {
          setShowAddUrl(false);
          setTimeout(() => setShowAdd(true), 200);
        }}
      />

      {/* ═══ Bottom Sheet : détail de l'article sélectionné ═══ */}
      {/* v1.12.55 : structure plate (backdrop + sheet siblings), sans nested */}
      {/* TouchableOpacity → ScrollView gère ses gestes sans interférence */}
      <Modal
        visible={!!selected}
        transparent
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setSelected(null)}
      >
        <View style={{ flex: 1 }}>
          {/* Backdrop semi-transparent — v1.12.168 : ne ferme PLUS sur clic
              outside (UX safe, evite les fermetures accidentelles). Le user
              doit cliquer X pour fermer. */}
          <View
            style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(0,0,0,0.4)' }]}
            pointerEvents="auto"
          />

          {/* Sheet : absolute en bas, par-dessus le backdrop */}
          <View
            style={[
              s.sheetContainer,
              {
                position: 'absolute',
                bottom: 0, left: 0, right: 0,
                maxHeight: '92%',
                paddingBottom: Math.max(30, insets.bottom + 20),
              },
            ]}
          >
            {(() => {
              const item = items.find(w => w.id === selected);
              if (!item) return null;
              const relatedLooks = looks.filter(l =>
                l.items.some(i => i.wardrobeId === item.id)
              );
              return (
                <>
                  {/* Bouton fermer en haut à droite — sticky en absolute, hors ScrollView */}
                  <TouchableOpacity
                    onPress={() => setSelected(null)}
                    hitSlop={{top:10,bottom:10,left:10,right:10}}
                    style={{ position: 'absolute', top: 14, right: 16, zIndex: 10, padding: 4 }}
                  >
                    <Text style={s.detailClose}>✕</Text>
                  </TouchableOpacity>

                  {/* Tout le reste scroll — flex:1 pour laisser de la place au footer sticky */}
                  <ScrollView
                    style={{ flex: 1 }}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ paddingBottom: 16 }}
                  >
                    {/* Handle bar */}
                    <View style={s.sheetHandle} />

                    {/* ═══ HERO : image complète (contain) + nom + marque centrés ═══ */}
                    {/* v1.12.52 : resizeMode contain → image entière visible, jamais coupée */}
                    <View style={s.detailHero}>
                      {item.image
                        ? <Image source={{ uri: getProxiedImageUrl(item.image) }} style={s.detailHeroImage} resizeMode="contain" />
                        : <View style={s.detailHeroImage}><Text style={{ fontSize: 60 }}>{item.emoji}</Text></View>
                      }
                      <Text style={s.detailHeroName} numberOfLines={2}>{displayName(item, lang)}</Text>
                      {!!item.brand && <Text style={s.detailHeroBrand}>{item.brand}</Text>}
                    </View>

                    <View style={s.detailChips}>
                      {(() => {
                        const CAT_KEYS = { 'Robes':'cat_dresses', 'Hauts':'cat_tops', 'Vestes':'cat_jackets', 'Manteaux':'cat_coats', 'Bas':'cat_bottoms', 'Maillots':'cat_swimwear', 'Chaussures':'cat_shoes', 'Sacs':'cat_bags', 'Bijoux':'cat_jewelry', 'Accessoires':'cat_accessories' };
                        const catLabel = item.category && CAT_KEYS[item.category] ? t(CAT_KEYS[item.category]) : item.category;
                        return !!item.category && <Badge label={catLabel} color="neutral" />;
                      })()}
                      {!!item.color && <Badge label={item.color} color="neutral" />}
                      {!!item.style && <Badge label={item.style} color="accent" />}
                    </View>

                    {/* ═══ SECTION ANALYSE IA ═══ */}
                    {/* Composant partagé avec ProductDetailModal et WardrobeItemDetailModal (v1.12.50) */}
                    {item.ai_metadata?.analyzedAt && (
                      <AIMetadataDetails aiMetadata={item.ai_metadata} />
                    )}

                    {/* Placeholder si l'analyse IA est pas encore faite */}
                    {!item.ai_metadata?.analyzedAt && (
                      <View style={{
                        marginTop: 14,
                        padding: 14,
                        backgroundColor: '#F5F5F5',
                        borderRadius: 12,
                        alignItems: 'center',
                      }}>
                        <Text style={{ fontSize: 24, marginBottom: 4 }}>⏳</Text>
                        <Text style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
                          {t('ai_analysis_in_progress_dots')}
                        </Text>
                      </View>
                    )}
                    {relatedLooks.length > 0 && (
                      <View style={s.detailLooks}>
                        <Text style={s.detailLooksTitle}>{t('used_in_looks_count').replace('{count}', String(relatedLooks.length))}</Text>
                        {relatedLooks.slice(0, 3).map(l => (
                          <Text key={l.id} style={s.detailLookName}>· {l.name}</Text>
                        ))}
                      </View>
                    )}
                  </ScrollView>

                  {/* v1.12.134 — Footer sticky : "Créer un look" + "Supprimer"
                      hors du ScrollView pour rester visibles en permanence. */}
                  <View style={s.detailFooter}>
                    <TouchableOpacity style={s.detailBtn}
                      onPress={() => {
                        const currentId = selected;
                        setSelected(null);
                        setPreselectForLook(currentId);
                        setTimeout(() => setShowCreateLook(true), 250);
                      }}>
                      <Text style={s.detailBtnText}>{t('create_a_look')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[s.detailBtn, s.detailBtnDanger]}
                      onPress={() => {
                        const itemSnapshot = item;
                        setSelected(null);
                        setTimeout(() => {
                          showAppAlert(
                            t('delete'),
                            t('confirm_remove_from_wardrobe').replace('{name}', itemSnapshot.name),
                            [
                              { text: t('cancel'), style: 'cancel' },
                              { text: t('delete'), style: 'destructive', onPress: async () => {
                                console.log('[Wardrobe] 🗑️ delete request for', itemSnapshot.id, itemSnapshot.name);
                                setItems(p => p.filter(i => i.id !== itemSnapshot.id));
                                try {
                                  const token = await getValidToken();
                                  if (!token) throw new Error('no token');
                                  await deleteWardrobeItemFull(itemSnapshot, token);
                                  console.log('[Wardrobe] ✓ delete OK for', itemSnapshot.id);
                                  try {
                                    const CACHE_KEY = `outfit_wardrobe_cache_${user.id}`;
                                    await AsyncStorage.removeItem(CACHE_KEY);
                                  } catch {}
                                  DeviceEventEmitter.emit('wardrobe_updated', { deletedId: itemSnapshot.id, _internalSkipReload: true });
                                  showAppAlert('✅', t('item_removed_success'));
                                } catch(e) {
                                  console.error('[Wardrobe] ❌ delete failed:', e.message);
                                  setItems(p => [...p, itemSnapshot]);
                                  showAppAlert(t('error'), t('cannot_delete') + ' : ' + e.message);
                                }
                              }},
                            ]
                          );
                        }, 250);
                      }}>
                      <Text style={s.detailBtnDangerText}>{t('delete')}</Text>
                    </TouchableOpacity>
                  </View>
                </>
              );
            })()}
          </View>
        </View>
      </Modal>

      {/* Modal créer un look */}
      <CreateLookModal
        visible={showCreateLook}
        onClose={() => {
          setShowCreateLook(false);
          setPreselectForLook(null);
        }}
        wardrobe={items || []}
        catalog={partnerProducts || []}
        gender={user?.gender || 'F'}
        preselectedItemId={preselectForLook}
        onProductPress={(p) => setSelectedProduct(p)}
        existingLookNames={(userCreatedLooks || []).map(l => l.name || '').filter(Boolean)}
        onLookCreated={async (newLook) => {
          // v1.12.286 — persistance DB via LookService (avec cache AsyncStorage)
          setUserCreatedLooks(prev => [newLook, ...prev.filter(l => l.id !== newLook.id)]);
          try {
            const { saveLook } = require('../services/LookService');
            await saveLook(newLook, { user, getValidToken });
          } catch(e) { console.warn('[Wardrobe] saveLook failed:', e?.message); }
          setPreselectForLook(null);
        }}
        onTryOnLook={(look) => {
          // Bascule vers Mes Looks puis ferme le modal (route try-on à brancher selon le wiring projet)
          setTab('mylooks');
        }}
        onAddToCalendar={(look) => {
          setCalendarShortcut({ look, source: 'created' });
        }}
        onShareLook={(look) => {
          try {
            const { Share } = require('react-native');
            Share.share({ message: `${look?.name || 'Mon look'} — MyTenue` });
          } catch {}
        }}
        // v1.12.282 — "Voir mes looks" : on bascule l'onglet sur "Mes Looks"
        onViewMyLooks={() => setTab('mylooks')}
        // v1.12.290 — try-on depuis CreateLookModal
        onTryOnLook={(look) => { openTryOnVertex(look); }}
        // v1.12.620 — Plus de gate strict ['basic','premium','gold'] : un Free
        // avec free_onboarding_tryon ou add-ons doit pouvoir essayer.
        // L'arbitrage est fait par openTryOnVertex via fetchTryOnUsage.
        canTryOn={true}
        onRequestSubscribe={() => {
          try {
            const { DeviceEventEmitter } = require('react-native');
            DeviceEventEmitter.emit('tryon_limit_reached', { tier: user?.tier || 'free', limit: 0, current: 0 });
          } catch {}
        }}
        // v1.12.310 — "Ajouter au dressing" depuis CreateLookModal : ouvre la modale d'ajout directement
        onAddToWardrobe={() => {
          setShowCreateLook(false);
          setTab('items');
          setTimeout(() => { try { handleOpenAdd(); } catch {} }, 250);
        }}
      />

      {/* v1.12.290 — TryOnModal local pour Mes Looks (boutons try-on dans la liste + Create Look sheet) */}
      <TryOnModal
        visible={!!tryOnOutfit}
        onClose={() => { setTryOnOutfit(null); setTryOnAuthToken(null); }}
        outfit={tryOnOutfit}
        userGender={user?.gender || 'F'}
        authToken={tryOnAuthToken}
        onUpgradePress={() => navigation?.navigate?.('Profile')}
        onProductPress={(product) => setSelectedProduct(product)}
      />

      {/* v1.12.250 — Raccourci calendrier depuis Mes Looks */}
      <AddOutfitToCalendarModal
        visible={!!calendarShortcut}
        preselectedLook={calendarShortcut?.look || null}
        preselectedSource={calendarShortcut?.source || null}
        onClose={() => setCalendarShortcut(null)}
        onAdded={() => {
          setCalendarShortcut(null);
          try { navigation.navigate('Calendar'); } catch {}
        }}
      />

      {/* Paywall Premium — nouveau UpgradeModal avec tier courant + propositions */}
      <UpgradeModal
        visible={showPremium}
        onClose={closePremium}
        currentTier={limitInfo?.tier || 'free'}
        feature={limitInfo?.trigger || premiumTrigger}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit } : null}
        onSubscribe={async (planId) => {
          const { activateSubscription } = require('../services/TestSubscribeService');
          await activateSubscription(planId, user, updateUserProfile);
        }}
      />
      {/* ═══ Modal détail produit partenaire (identique au Catalogue) ═══ */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          userId={user?.id || null}
          onClose={() => setSelectedProduct(null)}
          onBought={(p) => {
            setSelectedProduct(null);
            setBoughtProduct(p || selectedProduct);
          }}
        />
      )}
      {boughtProduct && (
        <PurchaseConfirmModal
          product={boughtProduct}
          onClose={() => setBoughtProduct(null)}
          onConfirmed={(item) => {
            setBoughtProduct(null);
            DeviceEventEmitter.emit('wardrobe_updated', { newItem: item });
          }}
        />
      )}
    </SafeAreaView>
  );
}

/* ══ Modal Ajout avec IA ════════════════════════════════════ */
function AddItemModal({ visible, onClose, onAdd, user, onAILimitReached, onPickUrl }) {
  const { t } = useI18n();
  // v1.12.89 — formulaire simplifié : photo détecte le nom + catégorie via IA,
  // l'utilisateur peut juste ajouter un pseudo optionnel pour le retrouver.
  // Plus d'ajout manuel sans photo : tout passe par photo / galerie / URL.
  // v1.12.119 + v1.12.204 — catégories gender-aware : pas de "Robes/Bijoux"
  // pour homme. "Manteaux" ajouté pour les deux genres (long > mi-cuisse).
  const isMale = (user?.gender || 'F') === 'M';
  const ADD_CATS = isMale
    ? ['Hauts','Vestes','Manteaux','Bas','Maillots','Chaussures','Sacs','Accessoires']
    : ['Robes','Hauts','Vestes','Manteaux','Bas','Maillots','Chaussures','Sacs','Accessoires','Bijoux'];
  const DEFAULT_CAT = isMale ? 'Hauts' : 'Robes';
  const [step, setStep] = useState('choose');  // 'choose' | 'photo-form'
  const [form, setForm] = useState({ pseudo: '', category: DEFAULT_CAT });
  // v1.10.83 : safe area insets pour éviter que le header soit caché
  // par la dynamic island (iOS) ou la barre de statut (Android).
  const insets = useSafeAreaInsets();

  const pickImage = async (useCamera) => {
    try {
      let result;
      if (useCamera) {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (perm.status !== 'granted') { Alert.alert(t('permission_required'), t('permission_camera_required')); return; }
        // v1.10.88 : Accepter TOUS les formats d'image (HEIC inclus sur iOS)
        // - mediaTypes: 'images' (string en SDK 55) accepte tout type d'image
        // - quality: 1.0 → on prend la qualité max ; on optimise ensuite côté JS
        // - allowsEditing: false → l'user gère son cadrage (pas de carré forcé)
        result = await ImagePicker.launchCameraAsync({
          mediaTypes: ['images'],
          quality: 0.8, // v1.13.79 — 1.0→0.8 : fichier plus léger = optim + base64 + upload plus rapides
          allowsEditing: false,
          exif: false, // pas besoin des métadonnées EXIF pour l'analyse
        });
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (perm.status !== 'granted') { Alert.alert(t('permission_required'), t('permission_gallery_required')); return; }
        result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          quality: 0.8, // v1.13.79 — 1.0→0.8 : fichier plus léger = optim + base64 + upload plus rapides
          allowsEditing: false,
          exif: false,
        });
      }
      if (!result.canceled && result.assets?.[0]) {
        const rawUri = result.assets[0].uri;

        // v1.13.79 — FIX "le popup ne s'affiche pas / trop lent" : avant, on
        //   AWAITait optimize + readAsStringAsync (plusieurs secondes sur une grosse
        //   image) AVANT setStep('photo-form') → le formulaire ne s'affichait
        //   qu'après tout ce travail (voire jamais sans quitter/revenir), et le gros
        //   base64 en state alourdissait tout. Maintenant : on affiche le formulaire
        //   IMMÉDIATEMENT (image brute), puis on optimise + lit le base64 en
        //   ARRIÈRE-PLAN (setForm quand prêt). Le parent gère photoB64=null (lit
        //   l'URI au moment de l'upload) donc rien n'est cassé si l'user valide vite.
        setForm(prev => ({ ...prev, image: rawUri, photoB64: null }));
        setStep('photo-form');

        (async () => {
          // Optimisation universelle (HEIC→JPEG, redim, compression)
          let finalUri = rawUri;
          try {
            const optimized = await optimizeImageForAI(rawUri);
            finalUri = optimized.uri;
            console.log(`[pickImage] ✓ image ${optimized.optimized ? 'optimisée' : 'brute'} (${optimized.width}×${optimized.height})`);
          } catch (eOpt) {
            console.warn(`[pickImage] ⚠️ optim échoué, image brute conservée : ${eOpt.message}`);
          }
          // Lire le fichier optimisé en base64 pour l'upload S3
          // ═══ FIX SDK 54 : import legacy (même raison que onAddItem) ═══
          let FileSystem;
          try {
            FileSystem = require('expo-file-system/legacy');
            if (!FileSystem.readAsStringAsync) FileSystem = require('expo-file-system');
          } catch {
            FileSystem = require('expo-file-system');
          }
          let b64 = null;
          try {
            const encoding = (FileSystem.EncodingType && FileSystem.EncodingType.Base64) || 'base64';
            b64 = await FileSystem.readAsStringAsync(finalUri, { encoding });
            const sizeMB = (b64.length / (1024 * 1024)).toFixed(2);
            console.log(`[pickImage] ✓ photoB64 lu (${sizeMB} MB en base64)`);
            if (b64.length > 4.5 * 1024 * 1024) {
              console.warn(`[pickImage] ⚠️ image > 4.5 MB après optim, le backend pourrait la rejeter`);
            }
          } catch (e) {
            console.warn(`[pickImage] ⚠️ readAsStringAsync failed: ${e.message}`);
          }
          // Mise à jour finale (image optimisée + base64) sans bloquer l'affichage
          setForm(prev => (prev.image === rawUri || prev.image === finalUri)
            ? { ...prev, image: finalUri, photoB64: b64 }
            : prev); // si l'user a changé d'image entre-temps, on n'écrase pas
        })();
      }
    } catch(e) {
      console.warn('pickImage:', e.message);
    }
  };

  // v1.12.105 — fonction runAI supprimée (dead code depuis v1.12.89, le nouveau
  // formulaire photo-form utilise analyzeItemImage côté parent onAddItem).

  const handleAdd = () => {
    // v1.12.89 — Le nom est détecté automatiquement par l'IA depuis la photo.
    // L'user peut juste optionnellement ajouter un pseudo pour le retrouver.
    if (!form.image) { Alert.alert(t('photo_required'), t('photo_required_msg')); return; }
    onAdd({
      emoji:    '👗',
      name:     '',  // ← vide : la metadata IA renseignera _suggestedName
      pseudo:   (form.pseudo || '').trim() || null,
      category: form.category,
      brand:    'Ma garde-robe',
      image:    form.image,
      photoB64: form.photoB64 || null,
    });
    setForm({ pseudo: '', category: DEFAULT_CAT, image: null, photoB64: null });
    setStep('choose');
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="overFullScreen" onRequestClose={() => { onClose(); setStep('choose'); }}>
      {/* v1.10.83 : View parent avec padding-top safe area (overFullScreen ignore SafeAreaView).
          Sur iOS dynamic island, insets.top ≈ 59px ; sur Android, ≈ 24px.
          On ajoute un fallback minimum de 20px pour les anciens devices. */}
      <View style={[s.modal, { paddingTop: Math.max(insets.top, 20) }]}>
        <View style={s.modalHeader}>
          <Text style={s.modalTitle}>{t('add_a_piece')}</Text>
          <TouchableOpacity
            onPress={() => { onClose(); setStep('choose'); }}
            style={s.modalCloseBtn}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={s.modalClose}>✕</Text>
          </TouchableOpacity>
        </View>
        <View style={s.modalDivider} />

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
        <ScrollView
          contentContainerStyle={{ padding: spacing.xl, paddingBottom: 80 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >

          {step === 'choose' && (
            <View style={s.chooseGrid}>
              {/* v1.12.123 — Camera : disponible uniquement sur mobile (iOS/Android).
                  Sur web, expo-image-picker.launchCameraAsync ne marche pas correctement
                  via les browsers (permissions, MediaStream API limitee). On masque.  */}
              {Platform.OS !== 'web' && (
                <TouchableOpacity style={[s.chooseCard, shadow.sm]} onPress={() => pickImage(true)}>
                  <Text style={s.chooseEmoji}>📷</Text>
                  <Text style={s.chooseTitle}>{t('take_a_photo')}</Text>
                  <Text style={s.chooseDesc}>{t('photograph_your_item')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[s.chooseCard, shadow.sm]} onPress={() => pickImage(false)}>
                <Text style={s.chooseEmoji}>🖼️</Text>
                <Text style={s.chooseTitle}>{t('from_gallery')}</Text>
                <Text style={s.chooseDesc}>{t('choose_existing_photo')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.chooseCard, shadow.sm]} onPress={() => { onClose(); setTimeout(() => onPickUrl?.(), 300); }}>
                <Text style={s.chooseEmoji}>🔗</Text>
                <Text style={s.chooseTitle}>{t('from_url')}</Text>
                <Text style={s.chooseDesc}>{t('instagram_facebook_article_link')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {step === 'photo-form' && (
            <>
              {/* Aperçu photo */}
              {form.image && (
                <View style={{ alignItems: 'center', marginBottom: spacing.lg }}>
                  <Image
                    source={{ uri: form.image }}
                    style={{
                      width: 180, height: 220,
                      borderRadius: radius.md,
                      backgroundColor: '#F5F2ED',
                    }}
                    resizeMode="cover"
                  />
                  <Text style={{
                    fontSize: 12, color: colors.text.secondary,
                    marginTop: 8, textAlign: 'center', fontStyle: 'italic',
                  }}>
                    {t('ai_will_detect_item_details')}
                  </Text>
                </View>
              )}

              {/* Catégorie */}
              <Text style={s.fieldLabel}>{t('category')}</Text>
              <View style={s.chipRow}>
                {ADD_CATS.map(c => {
                  const CAT_KEYS = { 'Robes':'cat_dresses', 'Hauts':'cat_tops', 'Vestes':'cat_jackets', 'Manteaux':'cat_coats', 'Bas':'cat_bottoms', 'Maillots':'cat_swimwear', 'Chaussures':'cat_shoes', 'Sacs':'cat_bags', 'Bijoux':'cat_jewelry', 'Accessoires':'cat_accessories' };
                  const cLabel = CAT_KEYS[c] ? t(CAT_KEYS[c]) : c;
                  return (
                  <TouchableOpacity key={c} style={[s.chip, form.category === c && s.chipActive]}
                    onPress={() => setForm(p => ({ ...p, category: c }))}>
                    <Text style={[s.chipText, form.category === c && s.chipTextActive]}>{cLabel}</Text>
                  </TouchableOpacity>
                  );
                })}
              </View>

              {/* Pseudo (optionnel) */}
              <Text style={s.fieldLabel}>{t('pseudo_optional')}</Text>
              <TextInput style={s.fieldInput}
                placeholder={t('pseudo_placeholder')}
                placeholderTextColor={colors.text.ghost}
                value={form.pseudo}
                onChangeText={v => setForm(p => ({ ...p, pseudo: v }))}
                autoCorrect={true}
                autoCapitalize="sentences"
                maxLength={60}
              />

              <CTAButton label={t('add_to_wardrobe_cta')} onPress={handleAdd}
                style={{ marginTop: spacing.lg }} />
              <TouchableOpacity style={s.backBtn} onPress={() => { setForm(p => ({ ...p, image: null, photoB64: null })); setStep('choose'); }}>
                <Text style={s.backBtnText}>{t('back_arrow')}</Text>
              </TouchableOpacity>
            </>
          )}
        </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

function getColorHex(color) {
  const map = {
    // Neutres
    'Noir':        '#1C1C1E',
    'Blanc':       '#F8F8F8',
    'Gris':        '#8E8E93',
    'Gris clair':  '#C7C7CC',
    'Gris foncé':  '#48484A',
    'Anthracite':  '#3A3A3C',
    'Crème':       '#FFF8E7',
    'Écru':        '#EDE8D0',
    'Ivoire':      '#FFFFF0',
    'Beige':       '#D4C5A9',
    'Camel':       '#C19A6B',
    'Taupe':       '#8B7355',
    'Marron':      '#6B4226',
    'Chocolat':    '#3D1C02',
    // Chauds
    'Or':          '#FFD700',
    'Doré':        '#DAA520',
    'Argent':      '#C0C0C0',
    'Bronze':      '#CD7F32',
    'Cuivre':      '#B87333',
    'Rouge':       '#C0392B',
    'Rouge vif':   '#E74C3C',
    'Bordeaux':    '#722F37',
    'Bourgogne':   '#800020',
    'Rose':        '#FFB6C1',
    'Rose fuchsia':'#FF1493',
    'Rose poudré': '#FADADD',
    'Corail':      '#FF6B6B',
    'Saumon':      '#FA8072',
    'Orange':      '#E67E22',
    'Rouille':     '#B7410E',
    'Terracotta':  '#C06F3A',
    'Jaune':       '#F1C40F',
    'Jaune moutarde': '#C8A200',
    // Froids
    'Bleu':        '#2980B9',
    'Bleu marine': '#001F5B',
    'Marine':      '#1B3A6B',
    'Navy':        '#001F5B',
    'Bleu ciel':   '#87CEEB',
    'Bleu roi':    '#4169E1',
    'Bleu nuit':   '#191970',
    'Bleu canard': '#00827F',
    'Turquoise':   '#40E0D0',
    'Aqua':        '#00FFFF',
    'Vert':        '#27AE60',
    'Vert forêt':  '#228B22',
    'Vert kaki':   '#6B7C45',
    'Kaki':        '#7D7C5F',
    'Olive':       '#808000',
    'Sauge':       '#9DAD7F',
    'Menthe':      '#98FF98',
    'Émeraude':    '#50C878',
    'Violet':      '#8E44AD',
    'Mauve':       '#DDA0DD',
    'Lavande':     '#E6E6FA',
    'Prune':       '#700B97',
    'Lilas':       '#C8A2C8',
    'Multicolore': '#FF9500',
  };
  if (!color) return '#E8E4DC';
  // Recherche exacte
  if (map[color]) return map[color];
  // Recherche partielle (insensible à la casse)
  const lower = color.toLowerCase();
  for (const [key, val] of Object.entries(map)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  return '#E8E4DC';
}

const s = StyleSheet.create({
  safe:       { flex: 1, backgroundColor: '#FFFFFF' },
  header:     { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.xl, paddingTop: spacing.md, paddingBottom: spacing.md },
  title:      { fontSize: 20, fontWeight: '300', color: colors.text.primary, letterSpacing: 0.5 },
  subtitle:   { fontSize: 11, color: colors.text.faint, letterSpacing: 0.5, marginTop: 2 },
  addBtn:     { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.text.primary, alignItems: 'center', justifyContent: 'center' },
  addBtnText: { fontSize: 20, color: '#fff', lineHeight: 22 },
  createLookBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#FFF9E6',
    borderWidth: 1, borderColor: '#F5DEB3',
    borderRadius: 100,
    paddingHorizontal: 12, paddingVertical: 8,
    marginRight: 8,
  },
  createLookIcon: { fontSize: 14 },
  createLookText: { fontSize: 11, color: '#B8860B', fontWeight: '700', letterSpacing: 0.5 },

  userLookCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1, borderColor: '#F5DEB3',
    marginBottom: 14,
    overflow: 'hidden',
  },
  userLookHeader: {
    flexDirection: 'row', alignItems: 'center',
    padding: 14,
    backgroundColor: '#FFF9E6',
    borderBottomWidth: 0.5, borderBottomColor: '#F5DEB3',
  },
  userLookOccasion: { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 2 },
  userLookName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  userLookDelete: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.6)' },
  userLookItem: { width: 80, alignItems: 'center' },
  userLookImg: { width: 80, height: 100, borderRadius: 6, backgroundColor: '#F5F5F5', resizeMode: 'cover' },
  userLookItemLabel: { fontSize: 9, color: '#666', marginTop: 4, textAlign: 'center' },
  userLookSuggsBlock: { borderTopWidth: 0.5, borderTopColor: '#F0F0F0', backgroundColor: '#FAFAFA', paddingTop: 10 },
  userLookSuggsTitle: { fontSize: 11, color: '#666', fontWeight: '600', paddingHorizontal: 12, marginBottom: 8 },
  userLookRefreshBtn: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderWidth: 1, borderColor: '#F5DEB3',
    alignItems: 'center', justifyContent: 'center',
    marginRight: 4,
  },
  userLookLoadMoreCard: {
    width: 110, height: 175,
    backgroundColor: '#FFF9E6',
    borderWidth: 1.5, borderColor: '#F5DEB3',
    borderStyle: 'dashed',
    borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    gap: 6, padding: 8,
  },
  userLookLoadMoreIcon: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: '#fff',
    borderWidth: 1, borderColor: '#F5DEB3',
    alignItems: 'center', justifyContent: 'center',
  },
  userLookLoadMoreText: { fontSize: 10, fontWeight: '700', color: '#B8860B', letterSpacing: 0.3, textAlign: 'center' },
  userLookDeleteBtn: {
    marginHorizontal: 14, marginVertical: 12,
    paddingVertical: 12, paddingHorizontal: 16,
    backgroundColor: '#FFF',
    borderWidth: 1, borderColor: '#FECACA',
    borderRadius: 100,
    alignItems: 'center', justifyContent: 'center',
  },
  userLookDeleteBtnText: { fontSize: 12, fontWeight: '700', color: '#DC2626', letterSpacing: 1, textTransform: 'uppercase' },
  userLookSuggCard: { width: 110, backgroundColor: '#fff', borderWidth: 1, borderColor: '#F0E4C4', borderRadius: 8, padding: 6 },
  userLookSuggImg: { width: '100%', height: 110, borderRadius: 5, backgroundColor: '#F5F5F5', resizeMode: 'cover' },
  userLookSuggBrand: { fontSize: 9, color: '#999', letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 4 },
  userLookSuggPrice: { fontSize: 11, color: '#B8860B', fontWeight: '700', marginTop: 2 },
  userLookFooter: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 12,
    borderTopWidth: 0.5, borderTopColor: '#F0E4C4',
    backgroundColor: '#fff',
  },
  userLookFooterLabel: { fontSize: 9, letterSpacing: 1.5, color: '#999', fontWeight: '600' },
  userLookFooterTotal: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', marginTop: 2 },
  userLookShopBtn: {
    backgroundColor: '#1A1A1A',
    paddingHorizontal: 22, paddingVertical: 12,
    borderRadius: 100,
  },
  userLookShopBtnText: { color: '#D4AF72', fontSize: 12, fontWeight: '700', letterSpacing: 1 },
  divider:    { height: 1, backgroundColor: colors.bg.border },

  tabs:       { flexDirection: 'row', paddingHorizontal: spacing.xl, paddingTop: spacing.md, gap: spacing.xl, borderBottomWidth: 1, borderBottomColor: colors.bg.border },
  tab:        { paddingBottom: spacing.sm, flexDirection: 'row', alignItems: 'center', gap: 6 },
  tabActive:  { borderBottomWidth: 2, borderBottomColor: colors.text.primary },
  tabLabel:   { fontSize: 13, color: colors.text.faint, letterSpacing: 0.3 },
  tabLabelActive: { color: colors.text.primary, fontWeight: '500' },
  tabBadge:    { minWidth: 20, height: 18, borderRadius: 9, backgroundColor: '#F0F0F0', paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center' },
  tabBadgeActive: { backgroundColor: colors.text.primary },
  tabBadgeText:   { fontSize: 10, color: '#999', fontWeight: '700' },
  tabBadgeTextActive: { color: '#FFF' },
  newLookBigBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: '#1A1A1A',
    borderWidth: 1, borderColor: '#D4AF72',
    borderRadius: radius.md,
    paddingVertical: 14,
    marginBottom: spacing.lg,
  },
  newLookBigBtnIcon: { fontSize: 20, color: '#D4AF72', fontWeight: '300' },
  newLookBigBtnText: { fontSize: 11, color: '#D4AF72', fontWeight: '700', letterSpacing: 2 },

  // Bandeau "Limite atteinte" en bas des onglets Looks/Suggestions
  combosLimitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#FFF9E6',
    borderWidth: 1,
    borderColor: '#F5DEB3',
    borderRadius: radius.md,
    padding: spacing.md,
    marginTop: spacing.lg,
    marginBottom: spacing.lg,
  },
  combosLimitBannerLeft: { flex: 1 },
  combosLimitBannerTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#B8860B',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  combosLimitBannerText: {
    fontSize: 11,
    color: '#8B6F2E',
    lineHeight: 16,
  },
  combosLimitBannerBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#1A1A1A',
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: '#D4AF72',
  },
  combosLimitBannerBtnText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#D4AF72',
    letterSpacing: 0.5,
  },

  searchRow:   { paddingHorizontal: spacing.xl, paddingTop: 6, paddingBottom: 0 },
  searchBox:  { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.bg.surface, borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, paddingHorizontal: spacing.md, height: 42 },
  searchIcon: { fontSize: 14, marginRight: 6 },
  searchInput:{ flex: 1, fontSize: 13, color: colors.text.primary },
  clearBtn:   { fontSize: 12, color: colors.text.faint, padding: 4 },

  catScrollWrap: { flexShrink: 0 },
  catRow:      { paddingHorizontal: spacing.xl, gap: 8, paddingTop: 4, paddingBottom: 8 },
  catChip:     { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.pill, borderWidth: 1.5, borderColor: colors.bg.border, backgroundColor: '#FFFFFF' },
  catChipActive: { borderColor: colors.text.primary, backgroundColor: colors.text.primary },
  catLabel:    { fontSize: 11, letterSpacing: 0.5, color: colors.text.secondary, fontWeight: '500' },
  catLabelActive: { color: '#fff' },

  grid:       { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: spacing.xl, gap: 10, paddingTop: 4 },
  gridCard:   { width: '47%', backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, overflow: 'hidden', position: 'relative' },
  gridCardSelected: { borderColor: colors.text.primary, borderWidth: 2 },
  // ═══ FIX 2026-04 (v1.7.57) : photos de mannequin entières ═══
  // Avant : height 120px + resizeMode 'cover' → la tête du mannequin était
  //   coupée sur les photos verticales (ratio ~3:4 cropé en 1.4:1).
  // Maintenant : height 200px + resizeMode 'contain' + fond gris doux pour
  //   les bandes haut/bas si la photo est moins haute. La pièce reste
  //   entièrement visible quel que soit le format d'origine (mannequin pied,
  //   plat-dos, packshot, etc.). Cohérent avec ProductDetailModal v1.7.42.
  gridImg:    { height: 200, backgroundColor: '#FAFAFA', alignItems: 'center', justifyContent: 'center' },
  gridImgPhoto:{ width: '100%', height: '100%', resizeMode: 'contain' },
  gridEmoji:  { fontSize: 40 },
  gridInfo:   { padding: spacing.sm },
  gridName:   { fontSize: 12, fontWeight: '500', color: colors.text.primary, marginBottom: 2 },
  gridBrand:  { fontSize: 10, color: colors.text.faint, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 },
  gridMeta:   { flexDirection: 'row', alignItems: 'center', gap: 4 },
  // v1.12.525 — Couleur principale IA en couleur, sous le nom
  gridColorRow:  { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 2 },
  gridColorBold: { fontSize: 11, fontWeight: '600', letterSpacing: 0.2, flex: 1 },
  colorDot:   { width: 10, height: 10, borderRadius: 5, borderWidth: 1, borderColor: colors.bg.border },
  gridColor:  { fontSize: 10, color: colors.text.faint },
  selectedBadge: { position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 11, backgroundColor: colors.text.primary, alignItems: 'center', justifyContent: 'center' },
  aiAnalyzedBadge: { position: 'absolute', top: 6, left: 6, backgroundColor: 'rgba(0,0,0,0.78)', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  aiAnalyzedText: { fontSize: 8, color: '#FFD700', fontWeight: '700', letterSpacing: 0.3 },
  selectedBadgeText: { fontSize: 10, color: '#fff', fontWeight: '700' },

  addCard:    { width: '47%', height: 168, borderRadius: radius.md, borderWidth: 2, borderColor: colors.bg.border, borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center', gap: 6 },
  addCardPlus:{ fontSize: 24, color: colors.text.ghost },
  addCardLabel:{ fontSize: 11, color: colors.text.ghost, letterSpacing: 0.5 },

  // ═══ Bottom sheet (article sélectionné) ═══
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  sheetContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    paddingHorizontal: spacing.xl,
    paddingTop: 10,
    paddingBottom: 30,
    // Shadow pour iOS + elevation Android
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 16,
  },
  sheetHandle: {
    width: 42, height: 4,
    borderRadius: 2,
    backgroundColor: '#E5E5E5',
    alignSelf: 'center',
    marginBottom: 14,
  },
  detailPanel:{ marginHorizontal: spacing.xl, marginTop: spacing.md, backgroundColor: '#FFFFFF', borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.lg, marginBottom: spacing.xl },
  detailHeader:{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: spacing.md },
  detailImage:{ width: 56, height: 56, borderRadius: 8, backgroundColor: '#F5F3F0' },
  detailEmoji:{ fontSize: 32 },
  detailInfo: { flex: 1 },
  detailName: { fontSize: 16, fontWeight: '500', color: colors.text.primary },
  detailBrand:{ fontSize: 11, color: colors.text.faint, letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 },

  // Nouveau layout "hero" : image grande + nom + marque centrés
  // v1.12.51 : taille 4:5 portrait (typique vêtements) avec contain mode → photo jamais coupée
  detailHero: { alignItems: 'center', marginBottom: spacing.md, marginTop: 4 },
  detailHeroImage: {
    width: 220,
    height: 275, // ratio 4:5
    borderRadius: 12,
    backgroundColor: '#F5F3F0',
    marginBottom: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailHeroName: { fontSize: 18, fontWeight: '600', color: colors.text.primary, textAlign: 'center', paddingHorizontal: 20 },
  detailHeroBrand: { fontSize: 11, color: colors.text.faint, letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 4 },

  detailClose:{ fontSize: 18, color: colors.text.faint, padding: 6 },
  detailChips:{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginBottom: spacing.md },
  detailLooks:{ backgroundColor: colors.bg.surface, borderRadius: radius.sm, padding: spacing.md, marginBottom: spacing.md },
  detailLooksTitle: { fontSize: 11, fontWeight: '600', color: colors.text.primary, marginBottom: 4 },
  detailLookName:{ fontSize: 12, color: colors.text.secondary, lineHeight: 20 },
  detailActions: { flexDirection: 'row', gap: 10, marginTop: 4 },
  detailFooter: {
    flexDirection: 'row', gap: 10,
    paddingTop: 12, paddingBottom: 4,
    marginHorizontal: -spacing.xl, paddingHorizontal: spacing.xl,
    borderTopWidth: 1, borderTopColor: colors.bg.border,
    backgroundColor: '#FFFFFF',
  },
  detailBtn:  { flex: 1, borderWidth: 1.5, borderColor: colors.text.primary, paddingVertical: 14, alignItems: 'center', borderRadius: 24 },
  detailBtnText: { fontSize: 12, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.8 },
  detailBtnDanger: { borderColor: colors.status.error, backgroundColor: '#fff' },
  detailBtnDangerText: { fontSize: 12, fontWeight: '700', color: colors.status.error, letterSpacing: 0.8 },

  comboSubtitle: { fontSize: 12, color: colors.text.faint, letterSpacing: 0.3, marginBottom: spacing.md },
  comboCard:  { backgroundColor: '#FFFFFF', borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.lg, marginBottom: spacing.md },
  comboHeader:{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  comboAiTipRow:{ flexDirection: 'row', alignItems: 'flex-start', gap: 6, paddingHorizontal: 14, paddingBottom: 10, marginTop: -4 },
  comboAiTipIcon:{ fontSize: 12, marginTop: 1 },
  comboAiTip:   { flex: 1, fontSize: 11, color: colors.accent.main, fontStyle: 'italic', lineHeight: 16 },
  comboName:  { fontSize: 15, fontWeight: '500', color: colors.text.primary },
  comboScoreRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scoreBar:   { width: 60, height: 3, backgroundColor: colors.bg.border, borderRadius: 2, overflow: 'hidden' },
  scoreBarFill: { height: '100%', backgroundColor: colors.accent.main, borderRadius: 2 },
  comboScore: { fontSize: 13, color: colors.text.secondary },
  comboItems: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.md },
  comboItem:  { alignItems: 'center', gap: 4, flex: 1 },
  comboItemBox: { width: 60, height: 72, borderRadius: radius.sm, backgroundColor: colors.bg.surface, borderWidth: 1, borderColor: colors.bg.border, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  comboItemBoxNew: { backgroundColor: colors.accent.ghost, borderColor: colors.accent.pale },
  comboItemImg: { width: '100%', height: '100%', resizeMode: 'cover' },
  comboItemEmoji: { fontSize: 22 },
  comboItemLabel: { fontSize: 9, color: colors.text.secondary, textAlign: 'center', letterSpacing: 0.2 },
  comboItemBrand: { fontSize: 8, color: colors.text.faint, letterSpacing: 0.5, textTransform: 'uppercase' },
  comboItemOwned: { fontSize: 9, color: colors.status.success, fontWeight: '600' },
  comboItemPrice: { fontSize: 9, fontWeight: '600', color: colors.accent.main },
  newItemBadge: { position: 'absolute', top: 3, right: 3, width: 14, height: 14, borderRadius: 7, backgroundColor: colors.accent.main, alignItems: 'center', justifyContent: 'center' },
  newItemBadgeText: { fontSize: 8, color: '#fff', fontWeight: '700' },
  comboFooter:{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  comboEmpty:       { padding: 20, alignItems: 'center' },
  comboEmptyTxt:    { fontSize: 12, color: colors.text.muted, textAlign: 'center' },
  comboItemsRow:    { paddingVertical: 8, gap: 10 },
  comboItemPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.bg.surface },
  ownedBadge:       { backgroundColor: '#E8F5E9', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  ownedBadgeTxt:    { fontSize: 10, color: '#2e7d32', fontWeight: '600' },
  newBadge:         { backgroundColor: '#1A1A1A', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  newBadgeTxt:      { fontSize: 11, color: '#fff', fontWeight: '700' },
  comboBuyBtnFull:  { backgroundColor: '#1A1A1A', borderRadius: 8, paddingVertical: 11, alignItems: 'center', flex: 1 },
  comboBuyBtnTxt:   { color: '#fff', fontWeight: '600', fontSize: 12 },
  comboBuyBtn:{ fontSize: 11, fontWeight: '600', color: colors.accent.main, letterSpacing: 0.5 },

  modal:      { flex: 1, backgroundColor: '#FFFFFF' },
  modalHeader:{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.xl, paddingVertical: spacing.lg },
  modalTitle: { fontSize: 18, fontWeight: '300', color: colors.text.primary, letterSpacing: 0.3 },
  aiLoadingBox:    { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#F0F7FF', borderRadius: 10, padding: 14, marginBottom: 16 },
  aiLoadingTxt:    { fontSize: 13, color: '#1976D2' },
  photoPreviewBox: { position: 'relative', marginBottom: 16, alignSelf: 'center' },
  photoPreview:    { width: 160, height: 200, borderRadius: 12, resizeMode: 'cover' },
  photoRemove:     { position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center' },
  photoRemoveTxt:  { color: '#fff', fontSize: 12, fontWeight: '700' },
  photoPickerRow:  { flexDirection: 'row', gap: 10, marginBottom: 16 },
  photoPickerBtn:  { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderWidth: 1.5, borderColor: '#E0E0E0', borderRadius: 10, paddingVertical: 12, borderStyle: 'dashed' },
  photoPickerIcon: { fontSize: 18 },
  photoPickerTxt:  { fontSize: 13, color: '#666' },
  // v1.10.83 : zone touch agrandie pour le bouton fermer (✕)
  // Sur iOS avec dynamic island, le ✕ peut être proche du bord donc on
  // assure une zone tactile minimum de 44×44 (recommandation Apple HIG).
  modalCloseBtn: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center', marginRight: -8 },
  modalClose: { fontSize: 22, color: colors.text.faint, fontWeight: '300' },
  modalDivider:{ height: 1, backgroundColor: colors.bg.border },

  chooseGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, marginTop: spacing.md },
  chooseCard: { width: '47%', flexGrow: 1, backgroundColor: colors.bg.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, padding: spacing.lg, alignItems: 'center', gap: 8 },
  chooseEmoji:{ fontSize: 36 },
  chooseTitle:{ fontSize: 15, fontWeight: '600', color: colors.text.primary },
  chooseDesc: { fontSize: 12, color: colors.text.faint, textAlign: 'center', lineHeight: 17 },

  fieldLabel: { fontSize: 10, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase', marginTop: spacing.lg, marginBottom: spacing.sm },
  fieldInput: { borderWidth: 1.5, borderColor: colors.bg.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: 12, fontSize: 14, color: colors.text.primary },

  aiBtn:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.text.primary, borderRadius: radius.sm, paddingVertical: 13, marginTop: spacing.md, gap: 8 },
  aiBtnText:  { fontSize: 13, fontWeight: '600', color: '#fff', letterSpacing: 0.5 },
  aiResult:   { backgroundColor: colors.accent.ghost, borderRadius: radius.sm, borderWidth: 1, borderColor: colors.accent.pale, padding: spacing.md, marginTop: spacing.sm },
  aiResultLabel: { fontSize: 9, letterSpacing: 2, color: colors.accent.dark, textTransform: 'uppercase', marginBottom: 4 },
  aiResultText: { fontSize: 12, color: colors.text.secondary, fontStyle: 'italic', lineHeight: 18 },

  chipRow:    { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip:       { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.pill, borderWidth: 1, borderColor: colors.bg.border },
  chipActive: { borderColor: colors.text.primary, backgroundColor: colors.text.primary },
  chipText:   { fontSize: 11, color: colors.text.secondary },
  chipTextActive: { color: '#fff', fontWeight: '500' },

  backBtn:    { alignItems: 'center', paddingVertical: spacing.md },
  backBtnText:{ fontSize: 12, color: colors.text.faint },

  // ═══════════ DRESSING BANNER + CATEGORIES PREMIUM ═══════════
  dressingStatsBanner: {
    flexDirection: 'row', alignItems: 'center',
    marginHorizontal: spacing.xl, marginTop: spacing.md, marginBottom: spacing.sm,
    backgroundColor: '#FFFFFF', borderRadius: radius.md,
    borderWidth: 1, borderColor: colors.bg.border,
    paddingVertical: spacing.md, paddingHorizontal: spacing.md,
  },
  dressingStatCell:    { flex: 1, alignItems: 'center' },
  dressingStatDiv:     { width: 1, height: 30, backgroundColor: colors.bg.border },
  dressingStatNum:     { fontSize: 18, fontWeight: '600', color: colors.text.primary },
  dressingStatLbl:     { fontSize: 8, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 3 },

  catChipPremium:      { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, borderRadius: radius.pill, backgroundColor: '#F5F3EE', borderWidth: 1, borderColor: 'transparent' },
  catChipPremiumActive:{ backgroundColor: colors.text.primary, borderColor: colors.text.primary },
  catLabelPremium:     { fontSize: 12, color: colors.text.secondary, fontWeight: '500', letterSpacing: 0.2 },
  catLabelPremiumActive:{ color: '#FFF', fontWeight: '600' },
  catCountBubble:      { minWidth: 20, height: 18, borderRadius: 9, backgroundColor: '#FFF', paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center' },
  catCountBubbleActive:{ backgroundColor: 'rgba(255,255,255,0.25)' },
  catCountText:        { fontSize: 10, color: colors.text.primary, fontWeight: '700' },
  catCountTextActive:  { color: '#FFF' },

  // ═══════════ CARTE PROGRESSION IA ═══════════
  aiProgressCard:         {
    marginHorizontal: spacing.xl, marginBottom: spacing.md,
    backgroundColor: '#FFF9E6',
    borderRadius: radius.md,
    padding: spacing.md,
    borderWidth: 1, borderColor: '#F5DEB3',
  },
  // v1.12.608 — V5.6 : bandeau items cachés
  hiddenBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginHorizontal: spacing.xl, marginBottom: spacing.md,
    backgroundColor: '#F4ECDD',
    borderRadius: radius.md,
    padding: spacing.md,
    borderWidth: 1.5, borderColor: '#D4AF37',
  },
  hiddenBannerIcon:  { fontSize: 22 },
  hiddenBannerTitle: { fontSize: 13, fontWeight: '700', color: '#1a1f36', marginBottom: 3 },
  hiddenBannerSub:   { fontSize: 11, color: '#666', lineHeight: 15 },
  hiddenBannerArrow: { fontSize: 22, color: '#D4AF37', fontWeight: '600' },
  aiProgressCardComplete: { backgroundColor: '#E8F5E9', borderColor: '#C8E6C9' },
  aiProgressHeader:       { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  aiProgressTitleRow:     { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3 },
  aiProgressIcon:         { fontSize: 15 },
  aiProgressTitle:        { fontSize: 13, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.2 },
  aiProgressSub:          { fontSize: 10.5, color: colors.text.secondary, lineHeight: 14 },
  aiProgressPctBadge:     {
    backgroundColor: '#D4AF37',
    paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: radius.sm, minWidth: 48, alignItems: 'center',
  },
  aiProgressPctBadgeComplete: { backgroundColor: '#4CAF50' },
  aiProgressPctText:      { fontSize: 13, fontWeight: '700', color: '#FFF', letterSpacing: 0.2 },
  aiProgressPctTextComplete: { color: '#FFF' },
  aiProgressBarWrap:      { marginBottom: 10 },
  aiProgressBarBg:        { height: 6, backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' },
  aiProgressBarFill:      { height: '100%', borderRadius: 3 },
  aiProgressFooter:       { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' },
  aiProgressFooterItem:   { flexDirection: 'row', alignItems: 'center', gap: 5 },
  aiProgressDot:          { width: 8, height: 8, borderRadius: 4 },
  aiProgressFooterText:   { fontSize: 10, color: colors.text.secondary },
  aiProgressFooterInfo:   { fontSize: 10, color: colors.text.faint, fontStyle: 'italic', marginLeft: 'auto' },

  // ═══════════ COMBINAISONS PREMIUM (inspired by Looks) ═══════════

  // Hub header
  comboHub:          { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, paddingBottom: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.bg.border },
  comboHubLeft:      { flex: 1, paddingRight: spacing.md },
  comboHubBadge:     { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  comboHubTitle:     { fontSize: 22, fontWeight: '300', color: colors.text.primary, letterSpacing: 0.3, marginBottom: 4 },
  comboHubSub:       { fontSize: 11, color: colors.text.faint, lineHeight: 15 },
  comboHubStat:      { alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, backgroundColor: '#F8F6F0', borderRadius: radius.md, borderWidth: 1, borderColor: '#E8E0C8', minWidth: 80 },
  comboHubStatNum:   { fontSize: 22, fontWeight: '600', color: '#B8860B' },
  comboHubStatLabel: { fontSize: 8, letterSpacing: 1, color: colors.text.faint, textTransform: 'uppercase', marginTop: 2 },

  // Section header
  comboSectionHeader:{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: spacing.md, paddingHorizontal: 4 },
  comboSectionIcon:  { fontSize: 20 },
  comboSectionTitle: { fontSize: 14, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.3 },
  comboSectionSub:   { fontSize: 10, color: colors.text.faint, marginTop: 2 },

  // État vide
  comboEmptyState:   { alignItems: 'center', padding: spacing.xl, borderRadius: radius.lg, backgroundColor: '#F9F7F3', borderWidth: 1, borderColor: colors.bg.border, borderStyle: 'dashed', marginVertical: spacing.lg },
  comboEmptyIcon:    { fontSize: 48, marginBottom: spacing.md },
  comboEmptyTitle:   { fontSize: 16, fontWeight: '500', color: colors.text.primary, marginBottom: 6 },
  comboEmptyDesc:    { fontSize: 12, color: colors.text.faint, textAlign: 'center', lineHeight: 18, marginBottom: spacing.lg },
  comboEmptyCta:     { paddingHorizontal: spacing.xl, paddingVertical: 10, backgroundColor: colors.text.primary, borderRadius: radius.pill },
  comboEmptyCtaText: { color: '#FFF', fontSize: 12, fontWeight: '600', letterSpacing: 0.3 },

  // Tenues mixtes (dressing + partenaires) — design premium
  comboLoadingRow:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12, paddingHorizontal: 4 },
  comboLoadingText:  { fontSize: 11, color: colors.accent.main },

  mixCombo:          { backgroundColor: '#FFFFFF', borderRadius: radius.lg, overflow: 'hidden', marginBottom: spacing.lg, borderWidth: 1, borderColor: colors.bg.border },
  mixComboHeader:    { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md, padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.bg.border },
  mixComboLabel:     { fontSize: 15, fontWeight: '600', color: colors.text.primary, letterSpacing: 0.2 },
  mixComboTipRow:    { flexDirection: 'row', alignItems: 'flex-start', gap: 5, marginTop: 6 },
  mixComboTipIcon:   { fontSize: 12 },
  mixComboTip:       { flex: 1, fontSize: 11, color: colors.text.secondary, fontStyle: 'italic', lineHeight: 15 },
  mixComboPriceBadge:{ backgroundColor: colors.text.primary, paddingHorizontal: 12, paddingVertical: 6, borderRadius: radius.sm, alignItems: 'center' },
  mixComboPriceLabel:{ fontSize: 7, letterSpacing: 1, color: '#FFD700', fontWeight: '700', marginBottom: 1 },
  mixComboPrice:     { fontSize: 13, fontWeight: '700', color: '#FFF' },

  mixComboItemsRow:  { gap: 10, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  mixComboItem:      { alignItems: 'center', width: 90 },
  mixComboItemBox:   { width: 84, height: 100, borderRadius: radius.sm, overflow: 'hidden', backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: colors.bg.border, position: 'relative' },
  mixComboItemBoxNew:{ borderColor: '#D4AF37', borderWidth: 2 },
  mixComboItemImg:   { width: '100%', height: '100%', resizeMode: 'cover' },
  mixComboItemPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  mixComboNewBadge:  { position: 'absolute', top: 4, right: 4, backgroundColor: '#D4AF37', paddingHorizontal: 5, paddingVertical: 2, borderRadius: 4 },
  mixComboNewBadgeText: { fontSize: 7, fontWeight: '700', color: '#FFF', letterSpacing: 0.3 },
  mixComboOwnedDot:  { position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: 8, backgroundColor: '#4CAF50', alignItems: 'center', justifyContent: 'center' },
  mixComboItemName:  { fontSize: 10, color: colors.text.primary, marginTop: 6, textAlign: 'center', fontWeight: '500' },
  mixComboItemPrice: { fontSize: 11, fontWeight: '700', color: '#D4AF37', marginTop: 2 },
  mixComboItemOwned: { fontSize: 10, color: '#4CAF50', marginTop: 2, fontWeight: '600' },

  mixComboCta:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, backgroundColor: '#F9F7F3', borderTopWidth: 1, borderTopColor: colors.bg.border },
  mixComboCtaText:   { fontSize: 13, fontWeight: '600', color: colors.text.primary, letterSpacing: 0.2 },
  mixComboCtaArrow:  { fontSize: 16, color: colors.text.primary, fontWeight: '700' },

  mixComboMetaRow:   { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 },
  mixComboMeta:      { fontSize: 11, color: colors.text.secondary },
  mixComboMetaDot:   { fontSize: 11, color: colors.text.faint },

  mixComboDivider:   { flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 20, marginHorizontal: 4 },
  mixComboDividerLine:{ flex: 1, width: 1, backgroundColor: '#E0E0E0' },
  mixComboDividerText:{ fontSize: 16, color: '#D4AF37', fontWeight: '700', marginVertical: 4 },
});
